<template>
  <div>
    <div class="row">
      <div class="medium-7 columns padding-xR">
        <div class="heading">{{ $t('rsvpDetails.edit.rideRetails') }}</div>
        <transition name="slide-down">
          <div class="geofence-label" v-if="isNewRsvp && !!currentGeofence">Geofence: <b>{{ currentGeofence }}</b></div>
        </transition>

        <div class="row">
          <div class="medium-6 columns">
            <div class="data-field">
              <div class="label">{{ $t('rsvpDetails.tripInformation.brand') }}*</div>
              <select v-model="editData.tripInfo.brandId" @change="changeBrand" :disabled="brandsLoading">
                <option :value="null" disabled :class="{'hideDesktop':isDesktop, 'showMobile':isMobile}"></option>
                <option v-for="brand in brands" :value="brand.id" :key="brand.id">{{ brand.name }}</option>
              </select>
            </div>
          </div>
          <div class="medium-6 columns" v-if="enableCharterBooking && isNewRsvp">
            <div class="data-field">
              <div class="label">Booking Type</div>
              <select v-model="bookingType">
                <option value="mileage">Mileage</option>
                <option value="charter">Charter</option>
              </select>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="small-12 columns">
            <div class="data-field">
              <div class="label">{{ $t('rsvpDetails.tripInformation.pickup') }}*</div>
              <div class="radio with-textbox">
                <i :class="{'far fa-circle fa-lg radio-fa':true, 'checked':isCustomPickupLocation}"
                  @click="isCustomPickupLocation = !isCustomPickupLocation"></i>
                <div class="small-8 columns padding-R">
                  <location :value="customPickupLocation" @change="locationNewValue"
                    :extra="pickupExtra" ref="customPickupLoc"></location>
                </div>
                <div class="small-4 columns padding-L">
                  <input type="text" class="input" :disabled="!isCustomPickupLocation || tripInfo.isAirportPickup"
                    :placeholder="$t('rsvpDetails.edit.aptGate')" v-model="editData.tripInfo.aptGate" />
                </div>
              </div>
            </div>
          </div>

          <div class="small-12 columns">
            <div class="data-field">
              <div class="radio with-textbox">
                <i :class="{'far fa-circle fa-lg radio-fa':true, 'checked':!isCustomPickupLocation}"
                  @click="isCustomPickupLocation = !isCustomPickupLocation"></i>
                <select :disabled="isCustomPickupLocation || waivePayments" v-model="pickupAirportId"
                  @change="selectAirportPickup">
                  <option value="0" disabled selected>Airport List</option>
                  <option v-for="airport in airportList" :value="airport.geofenceId">
                    {{ airport.description }}
                  </option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div class="row" v-if="!charter.enabled">
          <div class="small-12 columns">
            <div class="data-field">
              <div class="label" v-if="extraStops && extraStops.length">Extra Stop*</div>
              <location class="margin-B-10" v-for="stop in extraStops" :key="stop.id"
                :value="stop" @change="locationNewStop"
                :extra="extraData(stop)" :ref="`extraStop${stop.id}`"></location>
            </div>
            <div class="add-extra-stop">
              <span @click="addExtraStop" v-if="extraStops && extraStops.length === 0"><i class="fas fa-plus-circle"></i> Add extra stop</span>
              <span @click="removeExtraStop" v-else><i class="fas fa-minus-circle"></i> Remove extra stop</span>
            </div>
          </div>
        </div>

        <div class="row" v-if="!charter.enabled">
          <div class="small-12 columns">
            <div class="data-field">
              <div class="label">{{ $t('rsvpDetails.tripInformation.dropoff') }}*</div>
              <div class="radio with-textbox">
                <i :class="{'far fa-circle fa-lg radio-fa':true, 'checked':isCustomDropoffLocation}"
                  @click="isCustomDropoffLocation = !isCustomDropoffLocation"></i>
                <div class="small-8 columns padding-R">
                  <location :value="customDropoffLocation" @change="locationNewValue"
                    :extra="dropoffExtra" ref="customDropoffLoc"></location>
                </div>
                <div class="small-4 columns padding-L">
                  <input type="text" class="input" :disabled="!isCustomDropoffLocation || tripInfo.isAirportDropoff"
                    :placeholder="$t('rsvpDetails.edit.aptGate')" v-model="editData.tripInfo.aptGateDropoff" />
                </div>
              </div>
            </div>
          </div>

          <div class="small-12 columns">
            <div class="data-field">
              <div class="radio with-textbox">
                <i :class="{'far fa-circle fa-lg radio-fa':true, 'checked':!isCustomDropoffLocation}"
                  @click="isCustomDropoffLocation = !isCustomDropoffLocation"></i>
                <select :disabled="isCustomDropoffLocation" v-model="dropoffAirportId"
                  @change="selectAirportDropoff">
                  <option value="0" disabled selected>Airport List</option>
                  <option v-for="airport in airportList" :value="airport.geofenceId">
                    {{ airport.description }}
                  </option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div class="row" v-if="mtech.enabled && (airportPickup || airportDropoff)">
          <div class="small-12 columns">
            <div class="data-field">
              <div class="label">Flight Date*</div>
              <div class="row" style="margin-bottom: 0">
                <div class="pickup-time">
                  <div class="small-12 medium-4 columns">
                    <datepicker :format="calendarFormat"
                      :class="['datepicker inline', {'has-error':!!validation.pickupTime && false}]"
                      v-model="flightDate.date" :disabled-picker="_edit.serviceLoading"
                      v-on:input="changeFlightDate" :disabled="disabledDate"></datepicker>
                  </div>
                  <div class="small-12 medium-8 columns time-select">
                    <select v-model="flightDate.hour" :disabled="_edit.serviceLoading"
                      @change="updateFlightDate" :class="{'has-error':!!validation.pickupTime && false}">
                      <option v-for="(n, i) in maxHour" :value="leadingZero(n)" :key="i">{{ leadingZero(n) }}</option>
                    </select>
                    <select v-model="flightDate.minute" :disabled="_edit.serviceLoading"
                      @change="updateFlightDate" :class="{'has-error':!!validation.pickupTime && false}">
                      <option value="00">00</option>
                      <option v-for="(n, i) in 11" :value="leadingZero(n * 5)" :key="i">{{ leadingZero(n * 5) }}</option>
                    </select>
                    <select v-model="flightDate.amPm" :disabled="_edit.serviceLoading"
                      @change="updateFlightDate" :class="{'has-error':!!validation.pickupTime && false}"
                      v-if="!isMilitaryTime">
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </select>
                    <!-- <div class="error-msg">{{ validation.pickupTime }}</div> -->
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="small-12 columns">
            <div class="data-field">
              <div :class="{'custom-check':true, 'enabled':isDomestic}"
                @click="isDomestic = !isDomestic">
                <div><i class="fa fa-check"></i></div>
              </div>
              <span class="label" @click="isDomestic = !isDomestic" style="cursor: pointer">
                This is a domestic flight
              </span>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="small-12 columns" v-if="!charter.enabled">
            <div class="data-field">
              <div class="label">{{ $t('rsvpDetails.tripInformation.pickupDate') }}*</div>
              <div class="asap" v-if="!mtech.enabled">
                <div :class="{'custom-check':true, 'enabled':pickupAsap}"
                  @click="pickupAsapToggle">
                  <div><i class="fa fa-check"></i></div>
                </div>
                <span class="label" @click="pickupAsapToggle">
                  As Soon As Possible
                </span>
              </div>
              <div class="row" style="margin-bottom: 0">
                <div class="pickup-time">
                  <div class="small-12 medium-4 columns">
                    <datepicker :format="calendarFormat"
                      :class="{'datepicker inline':true, 'has-error':!!validation.pickupTime}"
                      v-model="pickupDate.date" :disabled-picker="tripInfo.isAsap || pickupAsap"
                      v-on:input="changeDate" :disabled="disabledDate"></datepicker>
                  </div>
                  <div class="small-12 medium-8 columns time-select">
                    <select v-model="pickupDate.hour" :disabled="tripInfo.isAsap || pickupAsap"
                      @change="updatePickupDate" :class="{'has-error':!!validation.pickupTime}">
                      <option v-for="n in maxHour" :value="leadingZero(n)">{{ leadingZero(n) }}</option>
                    </select>
                    <select v-model="pickupDate.minute" :disabled="tripInfo.isAsap || pickupAsap"
                      @change="updatePickupDate" :class="{'has-error':!!validation.pickupTime}">
                      <option value="00">00</option>
                      <option v-for="n in 11" :value="leadingZero(n * 5)">{{ leadingZero(n * 5) }}</option>
                    </select>
                    <select v-model="pickupDate.amPm" :disabled="tripInfo.isAsap || pickupAsap"
                      @change="updatePickupDate" :class="{'has-error':!!validation.pickupTime}"
                      v-if="!isMilitaryTime">
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </select>
                    <div class="error-msg">{{ validation.pickupTime }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="small-12 columns" v-if="charter.enabled">
            <div class="data-field">
              <div class="label">Start Date*</div>
              <div class="row" style="margin-bottom: 0">
                <div class="pickup-time">
                  <div class="small-12 medium-4 columns">
                    <datepicker :format="calendarFormat"
                      :class="{'datepicker inline':true, 'has-error':!!charterValidation.startDate}"
                      v-model="startDate.date" v-on:input="changeCharterDate('startDate')"
                      :disabled="disabledDate"></datepicker>
                  </div>
                  <div class="small-12 medium-8 columns">
                    <select v-model="startDate.hour"
                      @change="updateCharterTime('startDate')" :class="{'has-error':!!charterValidation.startDate}">
                      <option v-for="n in maxHour" :value="leadingZero(n)" :key="n">{{ leadingZero(n) }}</option>
                    </select>
                    <select v-model="startDate.minute"
                      @change="updateCharterTime('startDate')" :class="{'has-error':!!charterValidation.startDate}">
                      <option value="00">00</option>
                      <option v-for="n in 11" :value="leadingZero(n * 5)" :key="n">{{ leadingZero(n * 5) }}</option>
                    </select>
                    <select v-model="startDate.amPm"
                      @change="updateCharterTime('startDate')" :class="{'has-error':!!charterValidation.startDate}"
                      v-if="!isMilitaryTime">
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </select>
                    <div class="error-msg">{{ charterValidation.startDate }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="small-12 columns" v-if="charter.enabled" style="margin-top: 15px;">
            <div class="data-field">
              <div class="label">End Date*</div>
              <div class="row" style="margin-bottom: 0">
                <div class="pickup-time">
                  <div class="small-12 medium-4 columns">
                    <datepicker :format="calendarFormat"
                      :class="{'datepicker inline':true, 'has-error':!!charterValidation.endDate}"
                      v-model="endDate.date" v-on:input="changeCharterDate('endDate')" 
                      :disabled="disabledDate"></datepicker>
                  </div>
                  <div class="small-12 medium-8 columns">
                    <select v-model="endDate.hour"
                      @change="updateCharterTime('endDate')" :class="{'has-error':!!charterValidation.endDate}">
                      <option v-for="n in maxHour" :value="leadingZero(n)" :key="n">{{ leadingZero(n) }}</option>
                    </select>
                    <select v-model="endDate.minute"
                      @change="updateCharterTime('endDate')" :class="{'has-error':!!charterValidation.endDate}">
                      <option value="00">00</option>
                      <option v-for="n in 11" :value="leadingZero(n * 5)" :key="n">{{ leadingZero(n * 5) }}</option>
                    </select>
                    <select v-model="endDate.amPm"
                      @change="updateCharterTime('endDate')" :class="{'has-error':!!charterValidation.endDate}"
                      v-if="!isMilitaryTime">
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </select>
                    <div class="error-msg">{{ charterValidation.endDate }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <transition name="slide-down">
          <div class="row" v-show="showFlightInput()">
            <div class="small-6 columns padding-R">
              <div class="data-field">
                <div class="label">
                  {{ $t('rsvpDetails.edit.airline') }}
                  <span v-if="!isFreshDeliveries">*</span>
                </div>
                <flight type="airlines" :value="editData.tripInfo.airline"
                  :extra="airlinesExtra" :airlineLoading="airlineLoading"></flight>
                <!-- <input type="text" class="input"
                  v-model="editData.tripInfo.airline"/> -->
              </div>
            </div>

            <div class="small-6 columns padding-L">
              <div class="data-field">
                <div class="label">
                  {{ $t('rsvpDetails.edit.flight') }}
                  <span v-if="!isFreshDeliveries">*</span>
                </div>
                <flight type="flight" :value="editData.tripInfo.flightNo"
                  :extra="flightExtra" :airlineLoading="airlineLoading"></flight>
                <!-- <input type="text" class="input"
                  v-model="editData.tripInfo.flightNo"/> -->
              </div>
            </div>
          </div>
        </transition>

        <div class="row">
          <div class="small-6 columns padding-R">
            <div class="data-field">
              <div class="label">{{ $t('rsvpDetails.tripInformation.passenger') }}*</div>
              <select v-model="passenger">
                <option v-for="n in 9" :key="n" :value="n">{{ n }}</option>
                <option :value="27" v-if="!mtech.enabled">Up to 27</option>
                <option :value="34" v-if="!mtech.enabled">Up to 34</option>
                <option :value="56" v-if="!mtech.enabled">Up to 56</option>
                <option v-for="n in 5" :key="n + 9" v-if="mtech.enabled">{{ n + 9 }}</option>
              </select>
            </div>
          </div>

          <div class="small-6 columns padding-L">
            <div class="data-field">
              <div class="label">{{ $t('rsvpDetails.tripInformation.luggage') }}*</div>
              <select v-model="luggage">
                <option value="0">0</option>
                <option v-for="x in 50" :value="x" :key="x">{{x}}</option>
              </select>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="small-6 columns">
            <div class="data-field">
              <div :class="{'custom-check':true, 'enabled':wheelchairEnabled}"
                @click="wheelchairEnabled = !wheelchairEnabled">
                <div><i class="fa fa-check"></i></div>
              </div>
              <span class="label" @click="wheelchairEnabled = !wheelchairEnabled">
                {{ $t('rsvpDetails.edit.wheelchair') }}
              </span>
            </div>
          </div>

          <div class="small-6 columns">
            <div class="data-field">
              <div :class="{'custom-check':true, 'enabled':serviceDogEnabled}"
                @click="serviceDogEnabled = !serviceDogEnabled">
                <div><i class="fa fa-check"></i></div>
              </div>
              <span class="label" @click="serviceDogEnabled = !serviceDogEnabled">
                {{ $t('rsvpDetails.edit.serviceDog') }}
              </span>
            </div>
          </div>
        </div>

        <div :class="{'row margin-B':true, 'hideDesktop':isDesktop, 'showMobile':isMobile}">
          <div class="small-12 columns">
            <div class="data-field service-field">
              <div :class="{'label':true, 'has-error':!!validation.service}">{{ $t('rsvpDetails.edit.services') }}*</div>
              <span class="error-msg">{{ validation.service }}</span>
              <transition name="popin">
                <div class="service-loader" v-show="serviceLoading">
                  <i class="fa fa-spinner fa-spin fa-5x"></i>
                </div>
              </transition>
              <label v-for="(service, index) in servicePrices"
                @click="selectService(serviceDetails(index).code, service)"
                :class="{'radio pricing':true, 'active':selectedService === serviceDetails(index).code, 'disabled':serviceDisable(index, service) || service.status !== 'allowed'}"
              >
              <span v-if="!serviceAvailability(service).enabled || !(serviceAvailability(service).enabled && !serviceAvailability(service).eligible)">
                  <span :class="{'value':true, 'has-discount':price(service).hasDiscount, 'price-override':priceOverrideEnabled && !!priceOverride}">
                    {{ price(service).value }}
                    <div v-show="price(service).hasDiscount">
                      <span>{{ price(service).original }}</span>
                    </div>
                  </span>
                </span>
                <span v-else class="unavailable">
                  <span class="availability">
                    <span v-if="serviceAvailability(service).unavailable" class="available">
                      Not Available
                    </span>
                    <span v-else class="available">
                      <span v-if="serviceAvailability(service).after">
                        <span class="available">Available from</span>
                        <span class="date-time">{{serviceAvailability(service).after}}</span>
                      </span>
                      <span v-else>
                        <span class="available">Available until</span>
                        <span class="date-time">{{serviceAvailability(service).before}}</span>
                      </span>
                    </span>
                  </span>
                </span>
                <div class="service-desc">
                  <span class="service-label">{{ serviceDetails(index).label }}</span>
                  <div class="pax-luggage">
                    <div :class="{'item':true, 'disabled':maxPax[index]}"
                      :title="$t('rsvpDetails.tripInformation.passenger')">
                      <i class="fas fa-users"></i>
                      <span class="rule-value">{{ service.maxPassengers }}</span>
                    </div>
                    <div :class="{'item':true, 'disabled':maxLuggage[index]}"
                      :title="$t('rsvpDetails.tripInformation.luggage')">
                      <i class="fas fa-briefcase"></i>
                      <span class="rule-value">{{ service.maxLuggage }}</span>
                    </div>
                  </div>
                </div>
              </label>
            </div>
          </div>
        </div>

        <div :class="{'row':true, 'hideDesktop':isDesktop, 'showMobile':isMobile}" v-if="airportPickup && hasPickupType">
          <div class="medium-6 small-12 columns end" v-show="showCurbside && !forceMeetandGreet">
            <div class="data-field">
              <div class="radio label-only">
                <span class="pointer pickuptype-label" @click="isMeetAndGreet = false">
                  <i :class="{'far fa-circle fa-lg radio-fa':true, 'checked':!isMeetAndGreet}"></i>
                  {{ $t('rsvpDetails.edit.curbside') }}
                </span>
              </div>
            </div>
          </div>
          <div class="medium-6 small-12 columns end" v-show="pickupTypeData.meetAndGreet || forceMeetandGreet">
            <div class="data-field">
              <div class="radio label-only">
                <span class="pointer pickuptype-label" @click="isMeetAndGreet = true">
                  <i :class="{'far fa-circle fa-lg radio-fa':true, 'checked':isMeetAndGreet}"></i>
                  {{ $t('rsvpDetails.edit.meetAndGreet') }}
                  (
                  <b v-if="priceOverrideEnabled && !!priceOverride">$0</b>
                  <b v-else-if="pickupTypeData.pickupFee">${{toFixed(pickupTypeData.pickupFee)}}</b>
                  <b v-else>{{ $t('rsvpDetails.edit.complimentary') }}</b>)
                </span>
              </div>
            </div>
          </div>
        </div>

        <div class="heading margin-xT">Customer / Corporate Information</div>

        <div v-if="isNewRsvp" class="customer-option">
          <a class="button cta xsmall" @click="$store.commit('rsvpDetails/setShowSelectUser', true)">
            <span v-if="customerInfo.firstName">Change Selected User</span>
            <span v-else>Select Existing User</span>
          </a>
          <span class="or">or</span>
          <a :class="{'button cta warning xsmall':true, 'disabled':createUser.show && !customerInfo.firstName}" @click="initCreateUser"
            :disabled="createUser.show && !customerInfo.firstName">Create New User</a>
          <span class="or">or</span>
          <a class="button cta xsmall" @click="$store.commit('rsvpDetails/setShowSelectCorporate', true)">
            <span v-if="customerInfo.firstName">Change Selected Corporate</span>
            <span v-else>Select Corporate User</span>
          </a>
          <div>
            <span class="select-card error-msg">{{ validation.userSelected }}</span>
          </div>
        </div>

        <div v-if="customerInfo.firstName">
          <div class="row">
            <div class="small-8 columns">
              <div class="data-field">
                <div class="label">{{ $t('rsvpDetails.customerInformation.name') }}</div>
                <div class="value">
                  {{ customerInfo.firstName + ' ' +customerInfo.lastName }}
                </div>
              </div>
            </div>

            <div class="small-4 columns">
              <div class="data-field">
                <div class="label">{{ $t('rsvpDetails.customerInformation.id') }}</div>
                <div class="value">
                  <a v-bind:href="'/#/corporate/' + customerInfo.id" target="_blank" v-if="isCorporate">{{ customerInfo.id }}</a>
                  <a v-bind:href="'/#/consumer/' + customerInfo.id" target="_blank" v-else>{{ customerInfo.id }}</a>
                </div>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="small-8 columns">
              <div class="data-field">
                <div class="label">{{ $t('rsvpDetails.customerInformation.email') }}</div>
                <div class="value">
                  <a v-bind:href="'mailto:' + customerInfo.email">{{ customerInfo.email }}</a>
                </div>
              </div>
            </div>

            <div class="small-4 columns">
              <div class="data-field">
                <div class="label">{{ $t('rsvpDetails.customerInformation.mobile') }}</div>
                <div class="value">
                  <a @click="call(customerInfo)">+{{ customerInfo.countryCode }} {{ customerInfo.nationalNumber || customerInfo.mobile }}</a>
                </div>
              </div>
            </div>
          </div>

          <div class="row margin-B-10" v-if="!isNewRsvp">
            <div class="small-12 columns">
              <div class="data-field padding-N-r">
                <div class="label">PAYING with</div>
                <div class="value">
                  <div>
                    <span v-bind:class="['cc-icon', cardIcon]"></span>
                    {{creditCard}}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="createUser.show && !customerInfo.firstName">
          <div class="row">
            <div class="small-6 columns margin-B">
              <div class="data-field">
                <div class="label">First Name*</div>
                <input type="text" :class="{'input':true, 'has-error':!!validation.userFirstName}" v-model="createUser.firstName" />
                <span class="error-msg">{{ validation.userFirstName }}</span>
              </div>
            </div>

            <div class="small-6 columns margin-B">
              <div class="data-field">
                <div class="label">Last Name*</div>
                <input type="text" :class="{'input':true, 'has-error':!!validation.userLastName}" v-model="createUser.lastName" />
                <span class="error-msg">{{ validation.userLastName }}</span>
              </div>
            </div>

            <div class="small-6 columns">
              <div class="data-field">
                <div class="label">Email*</div>
                <input type="text" :class="{'input':true, 'has-error':!!validation.userEmail}" v-model="createUser.email" />
                <span class="error-msg">{{ validation.userEmail }}</span>
              </div>
            </div>

            <div class="small-6 columns">
              <div class="data-field">
                <div class="label">Mobile*</div>
                <!-- <input type="text" :class="{'input':true, 'has-error':!!validation.userMobile}" v-model="createUser.mobile" /> -->
                <div class="input-group">
                  <div class="country-select input-group-label" @click="selectCountry">
                    <span class="country-code">(+{{callingCode}}) </span>
                    <i class="fa fa-caret-down"></i>
                  </div>
                  <input type="text" :class="['input', {'has-error':!!validation.userMobile}]" v-model="createUser.mobile" />
                </div>
                <span class="error-msg">{{ validation.userMobile }}</span>
              </div>
            </div>
          </div>

          <div class="row row-d2d" v-if="isD2D">
            <div class="small-6 columns margin-B">
              <div class="data-field">
                <div class="label">Birthday*</div>
                <datepicker initialView="year"
                  v-model="d2dUser.birthday" :format="calendarFormat"
                  input-class="input"
                  :class="['datepicker inline', {'has-error':!!d2dValidation.birthday}]"
                ></datepicker>
                <span class="error-msg">{{ d2dValidation.birthday }}</span>
              </div>
            </div>
            <div class="small-6 columns margin-B">
              <div class="data-field">
                <div class="label">Passport (Optional)</div>
                <input type="text" class="input" v-model="d2dUser.passport" />
              </div>
            </div>
          </div>

          <div class="row row-d2d" v-if="isD2D">
            <div class="small-6 columns margin-B">
              <div class="data-field">
                <div class="label">Address*</div>
                <input type="text" :class="['input', {'has-error':!!d2dValidation.address}]" v-model="d2dUser.address" />
                <span class="error-msg">{{ d2dValidation.address }}</span>
              </div>
            </div>
            <div class="small-6 columns margin-B">
              <div class="data-field">
                <div class="label">City*</div>
                <input type="text" :class="['input', {'has-error':!!d2dValidation.city}]" v-model="d2dUser.city" />
                <span class="error-msg">{{ d2dValidation.city }}</span>
              </div>
            </div>
          </div>

          <div class="row row-d2d" v-if="isD2D">
            <div class="small-6 columns margin-B">
              <div class="data-field">
                <div class="label">Zip*</div>
                <input type="text" :class="['input', {'has-error':!!d2dValidation.zip}]" v-model="d2dUser.zip" />
                <span class="error-msg">{{ d2dValidation.zip }}</span>
              </div>
            </div>
            <div class="small-6 columns margin-B">
              <div class="data-field">
                <div class="label">Country*</div>
                <select :class="['country-selector', {'has-error':d2dValidation.country}]" v-model="d2dUser.country" placeholder="Country">
                  <option value="" disabled>Select Country</option>
                  <option v-for="(country, i) in countryList" :key="i" :value="country.code.toUpperCase()">{{cleanCountryName(country.name)}}</option>
                </select>
                <span class="error-msg">{{ d2dValidation.country }}</span>
              </div>
            </div>
          </div>
        </div>

        <div :class="{'row guest':true, 'hideDesktop':isDesktop, 'showMobile':isMobile}">
          <div class="small-12 columns">
            <div class="data-field">
              <div :class="{'custom-check':true, 'enabled':bookingForSomeone}"
                @click="bookingForSomeone = !bookingForSomeone">
                <div><i class="fa fa-check"></i></div>
              </div>
              <span class="label" @click="bookingForSomeone = !bookingForSomeone">
                {{ $t('rsvpDetails.edit.bookingForSomeone') }}
              </span>
            </div>
          </div>

          <transition name="slide-down">
            <div v-show="bookingForSomeone">
              <div>
                <div class="small-6 columns margin-T">
                  <div class="data-field">
                    <div class="label">{{ $t('rsvpDetails.edit.guestFirstName') }}*</div>
                    <input type="text" v-model="editData.guestInfo.firstName"
                      :class="{'input':true, 'has-error':!!validation.guestFirstName}" />
                    <span class="error-msg">{{ validation.guestFirstName }}</span>
                  </div>
                </div>

                <div class="small-6 columns margin-T">
                  <div class="data-field">
                    <div class="label">{{ $t('rsvpDetails.edit.guestLastName') }}*</div>
                    <input type="text" v-model="editData.guestInfo.lastName"
                      :class="{'input':true, 'has-error':!!validation.guestLastName}" />
                    <span class="error-msg">{{ validation.guestLastName }}</span>
                  </div>
                </div>

                <div class="small-12 columns margin-T">
                  <div class="data-field">
                    <div class="label">{{ $t('rsvpDetails.edit.guestMobile') }}</div>
                    <!-- <div class="input-group">
                      <div class="country-select input-group-label" @click="selectCountry">
                        <span class="country-code">(+{{callingCode}}) </span>
                        <i class="fa fa-caret-down"></i>
                      </div>
                      <input type="text" :class="['input', {'has-error':validation.guestMobile}]" v-model="editData.guestInfo.mobile" />
                    </div> -->
                    <input type="text" v-model="editData.guestInfo.mobile"
                      :class="{'input':true, 'has-error':!!validation.guestMobile}" />
                    <span class="error-msg">{{ validation.guestMobile }}</span>
                  </div>
                </div>
              </div>
            </div>
          </transition>
        </div>

        <div>
          <div class="heading margin-xT">Billing Info</div>

          <div class="row margin-B-0 payment-option-box" v-if="isNewRsvp">
            <!-- <div class="small-12 columns margin-B">
              <div class="data-field">
                <div class="label payment-line"><span>Payment</span></div>
              </div>
            </div> -->

            <div class="small-12 columns" v-show="isCorporate">
              <div class="data-field margin-B">
                <div class="radio with-textbox" @click="createUser.paymentMethod = 'directBilling'">
                  <i :class="{'far fa-circle fa-lg radio-fa':true, 'checked':createUser.paymentMethod === 'directBilling'}"></i>
                  <span class="payment-option">Direct Billing</span>
                </div>
              </div>
            </div>

            <div class="small-12 columns">
              <div class="data-field margin-B">
                <div class="radio with-textbox" @click="createUser.paymentMethod = 'otherMeans'">
                  <i :class="{'far fa-circle fa-lg radio-fa':true, 'checked':createUser.paymentMethod === 'otherMeans'}"></i>
                  <span class="payment-option">Customer already paid through other means</span>
                </div>
              </div>
            </div>
            <div class="clearfix"></div>
            <transition name="slide-down">
              <div class="cc-form show" v-show="createUser.paymentMethod === 'otherMeans'">
                <div class="small-12 columns margin-B">
                  <div class="data-field">
                    <div class="label">Notes</div>
                    <textarea v-model="createUser.notes" :class="{'paid-notes':true, 'has-error':!!validation.userNotes}"
                      v-on:keypress="charCountChecker" v-on:keyup="charCountChecker"></textarea>
                    <span class="notes-char-count">{{ noteCounter }} character(s) left</span>
                    <span class="error-msg">{{ validation.userNotes }}</span>
                  </div>
                </div>

                <div class="small-12 columns margin-B">
                  <div class="data-field">
                    <div class="label">Status</div>
                    <select v-model="createUser.noteStatus">
                      <option :value="null" disabled :class="{'hideDesktop':isDesktop, 'showMobile':isMobile}"></option>
                      <option value="new">{{ $t('rsvpNotes.new') }}</option>
                      <option value="investigation">{{ $t('rsvpNotes.investigation') }}</option>
                      <option value="resolved">{{ $t('rsvpNotes.resolved') }}</option>
                    </select>
                    <span class="error-msg">{{ validation.userNotesStatus }}</span>
                  </div>
                </div>
                <div class="clearfix"></div>
              </div>
            </transition>

            <div class="small-12 columns">
              <div class="data-field margin-B">
                <div class="radio with-textbox" @click="createUser.paymentMethod = 'newCard'">
                  <i :class="{'far fa-circle fa-lg radio-fa':true, 'checked':createUser.paymentMethod === 'newCard'}"></i>
                  <span class="payment-option">Charge customer using a new card</span>
                </div>
              </div>
            </div>
            <div class="clearfix"></div>
            <transition name="slide-down">
              <div class="cc-form show" v-show="createUser.paymentMethod === 'newCard' && !isD2D">
                <div class="small-12 columns margin-B">
                  <div class="data-field">
                    <div class="label">Cardholder Name</div>
                    <input type="text" :class="{'input':true, 'has-error':!!validation.userCardOwner}" v-model="createUser.cardOwner" />
                    <span class="error-msg">{{ validation.userCardOwner }}</span>
                  </div>
                </div>

                <div class="small-12 columns margin-B">
                  <div class="data-field">
                    <div class="label">Credit Card #</div>
                    <input type="text" :class="{'input':true, 'has-error':!!validation.userCreditCard}" v-model="createUser.creditCard" />
                    <span class="error-msg">{{ validation.userCreditCard }}</span>
                  </div>
                </div>

                <div class="small-6 columns margin-B">
                  <div class="data-field">
                    <div class="label">Expiration (MM/YYYY)</div>
                    <input type="text" :class="{'input':true, 'has-error':!!validation.userCardExp}" v-model="createUser.cardExp"
                      v-on:keyup="formatExpCC" v-on:keypress="isNumber"/>
                    <span class="error-msg">{{ validation.userCardExp }}</span>
                  </div>
                </div>
                <div class="small-6 columns margin-B">
                  <div class="data-field">
                    <div class="label">CVV</div>
                    <input type="text" :class="{'input':true, 'has-error':!!validation.userCvv}" v-model="createUser.cvv" v-on:keypress="isNumber" />
                    <span class="error-msg">{{ validation.userCvv }}</span>
                  </div>
                </div>

                <div class="small-12 columns margin-B">
                  <div class="data-field">
                    <div class="label">Address</div>
                    <input type="text" :class="{'input':true, 'has-error':!!validation.userAddress}" v-model="createUser.address" />
                    <span class="error-msg">{{ validation.userAddress }}</span>
                  </div>
                </div>

                <div class="small-12 columns margin-B">
                  <div class="data-field">
                    <div class="label">City</div>
                    <input type="text" :class="{'input':true, 'has-error':!!validation.userCity}" v-model="createUser.city" />
                    <span class="error-msg">{{ validation.userCity }}</span>
                  </div>
                </div>

                <div class="small-6 columns margin-B">
                  <div class="data-field">
                    <div class="label">State</div>
                    <!-- @change="fieldValidate('businessInfo', 'state')" -->
                    <select v-model="createUser.state" :class="{'has-error':!!validation.userState}">
                      <option :value="null" disabled :class="{'hideDesktop':isDesktop, 'showMobile':isMobile}"></option>
                      <option value=".."></option>
                      <option value="">Non US</option>
                      <option v-for="state in states" :value="state.value">{{state.label}}</option>
                    </select>
                    <span class="error-msg">{{ validation.userState }}</span>
                  </div>
                </div>
                <div class="small-6 columns margin-B">
                  <div class="data-field">
                    <div class="label">Zip</div>
                    <input type="text" :class="{'input':true, 'has-error':!!validation.userZip}" v-model="createUser.zip" />
                    <span class="error-msg">{{ validation.userZip }}</span>
                  </div>
                </div>
                <div class="small-6 columns margin-B" v-show="createUser.state === ''">
                  <div class="data-field col col50">
                    <div class="label">Country</div>
                    <select v-model="createUser.country" :class="{'input':true, 'has-error':!!validation.userCountry}">
                      <option :value="null" disabled :class="{'hideDesktop':isDesktop, 'showMobile':isMobile}"></option>
                      <option v-for="country in countries.all">{{country.name}}</option>
                    </select>
                    <span class="error-msg">{{ validation.userCountry }}</span>
                  </div>
                </div>

                <div class="clearfix"></div>
              </div>
            </transition>
            <transition name="slide-down">
              <stripe-form v-show="createUser.paymentMethod === 'newCard' && isD2D" ref="stripeForm"
                @cardAdded="setNewD2DCard"
              ></stripe-form>
            </transition>

            <div v-if="(!!customerInfo.firstName || isCorporate) && userCardlist.length > 0">
              <div class="small-12 columns">
                <div class="data-field margin-B">
                  <div class="radio with-textbox" @click="createUser.paymentMethod = 'oldCard'">
                    <i :class="{'far fa-circle fa-lg radio-fa':true, 'checked':createUser.paymentMethod === 'oldCard'}"></i>
                    <span class="payment-option">Charge customer using his/her credit card</span>
                  </div>
                </div>
              </div>
              <div class="clearfix"></div>
              <transition name="slide-down">
                <div class="row margin-B-10" v-show="createUser.paymentMethod === 'oldCard'">
                  <div class="small-12 columns">
                    <div class="data-field padding-N-r">
                      <div class="label">PAYING with</div>
                      <div class="value">
                        <select v-model="createUser.selectedCard" :disabled="userCardLoading"
                          :class="{'has-error': !!validation.creditCard}">
                          <!-- <option value="new"><i>Add New Card</i></option> -->
                          <option :value="null" disabled :class="{'hideDesktop':isDesktop, 'showMobile':isMobile}"></option>
                          <option v-for="(card, index) in userCardlist" :value="index">{{ card.name }}</option>
                        </select>
                        <span class="error-msg">{{ validation.creditCard }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </transition>
            </div>

          </div>
        </div>

        <div :class="{'row guest':true, 'showDesktop':isDesktop, 'hideMobile':isMobile}">
          <div class="small-12 columns" v-if="!isCorporate">
            <div class="data-field">
              <div :class="{'custom-check':true, 'enabled':bookingForSomeone}"
                @click="bookingForSomeone = !bookingForSomeone">
                <div><i class="fa fa-check"></i></div>
              </div>
              <span class="label" @click="bookingForSomeone = !bookingForSomeone">
                {{ $t('rsvpDetails.edit.bookingForSomeone') }}
              </span>
            </div>
          </div>

          <transition name="slide-down">
            <div v-show="bookingForSomeone || isCorporate">
              <div>
                <div class="small-6 columns margin-T">
                  <div class="data-field">
                    <div class="label">{{ $t('rsvpDetails.edit.guestFirstName') }} *</div>
                    <input type="text" v-model="editData.guestInfo.firstName"
                      :class="{'input':true, 'has-error':!!validation.guestFirstName}" />
                    <span class="error-msg">{{ validation.guestFirstName }}</span>
                  </div>
                </div>

                <div class="small-6 columns margin-T">
                  <div class="data-field">
                    <div class="label">{{ $t('rsvpDetails.edit.guestLastName') }} *</div>
                    <input type="text" v-model="editData.guestInfo.lastName"
                      :class="{'input':true, 'has-error':!!validation.guestLastName}" />
                    <span class="error-msg">{{ validation.guestLastName }}</span>
                  </div>
                </div>

                <div class="small-12 columns margin-T">
                  <div class="data-field">
                    <div class="label">
                      {{ $t('rsvpDetails.edit.guestMobile') }}
                      <span v-if="isCorporate"> *</span>
                    </div>
                    <!-- <div class="input-group">
                      <div class="country-select input-group-label" @click="selectCountry">
                        <span class="country-code">(+{{callingCode}}) </span>
                        <i class="fa fa-caret-down"></i>
                      </div>
                      <input type="text" :class="['input', {'has-error':validation.guestMobile}]" v-model="editData.guestInfo.mobile" />
                    </div> -->
                    <input type="text" v-model="editData.guestInfo.mobile"
                      :class="{'input':true, 'has-error':!!validation.guestMobile}" />
                    <span class="error-msg">{{ validation.guestMobile }}</span>
                  </div>
                </div>
              </div>
            </div>
          </transition>
        </div>

        <div class="row">
          <div class="small-12 columns">
            <div class="data-field">
              <div class="label">{{ $t('rsvpDetails.edit.driverNotes') }}</div>
              <textarea v-model="editData.tripInfo.notes"></textarea>
            </div>
          </div>

          <div class="small-12 columns send-email" v-if="!isNewRsvp">
            <div class="data-field">
              <div :class="{'custom-check':true, 'enabled':sendEmailReminder}"
                @click="sendEmailReminder = !sendEmailReminder">
                <div><i class="fa fa-check"></i></div>
              </div>
              <span class="label" @click="sendEmailReminder = !sendEmailReminder">Send email and sms update</span>
            </div>
          </div>
        </div>
      </div>

      <div class="medium-5 columns padding-xL customer-info">
        <div class="row margin-B" v-if="!isNewRsvp && hasWaivePaymentAccess && !mtech.enabled">
          <div class="medium-12 columns waive-payment">
            <div :class="['custom-check', {'enabled':waivePayments}]"
              @click="waivePayments = !waivePayments">
              <div><i class="fa fa-check"></i></div>
            </div>
            <label for="waivePayment" @click="waivePayments = !waivePayments">Waive edit payments</label>
          </div>
        </div>

        <div class="row margin-B">
          <div class="medium-12 columns">
            <div class="data-field">
              <div class="label">Currency</div>
              <select v-model="currencyCode" :disabled="rsvpId !== 'new'">
                <option value="USD">$ (USD)</option>
                <option value="INR">{{ currencySymbol.INR }} (INR)</option>
                <option value="CAD">{{ currencySymbol.CAD }} (CAD)</option>
                <option value="EUR">{{ currencySymbol.EUR }} (EUR)</option>
                <option value="GBP">{{ currencySymbol.GBP }} (GBP)</option>
                <option value="JPY">{{ currencySymbol.JPY }} (JPY)</option>
                <option value="CNY">{{ currencySymbol.CNY }} (CNY)</option>
                <option value="AUD">{{ currencySymbol.AUD }} (AUD)</option>
                <option value="AED">{{ currencySymbol.AED }} (AED)</option>
                <option value="MXN">{{ currencySymbol.MXN }} (MXN)</option>
                <option value="PHP">{{ currencySymbol.PHP }} (PHP)</option>
              </select>
            </div>
          </div>
        </div>

        <div :class="['row margin-B', {'showDesktop':isDesktop, 'hideMobile':isMobile}]">
          <div class="small-12 columns">
            <div class="data-field service-field">
              <div :class="['label', {'has-error':!!validation.service}]">{{ $t('rsvpDetails.edit.services') }}*</div>
              <div class="extra-stop-notice" v-if="!mtech.enabled && extraStops && extraStops.length">
                Shared service is not allowed on booking with Extra stops.
              </div>
              <span class="error-msg">{{ validation.service }}</span>
              <transition name="popin">
                <div class="service-loader" v-show="serviceLoading">
                  <i class="fa fa-spinner fa-spin fa-5x"></i>
                </div>
              </transition>

              <div v-if="mtech.enabled">
                <label v-for="(service, index) in mtech.services" :key="index"
                  @click="selectMtechService(service)"
                  :class="['radio pricing', {'active':selectedService === service.code, 'disabled':passenger > service.maxPaxVal || luggage > service.maxLuggageVal}]">
                  <span>
                    <span :class="['value', {'has-discount':service.price.hasDiscount, 'price-override':priceOverrideEnabled && !!priceOverride}]">
                      {{ applyCurrency(toFixed(service.price.baseFare + service.price.addMarkup - service.price.discountVal)) }}
                      <div v-show="service.price.hasDiscount">
                        <span>{{ applyCurrency(toFixed(service.price.baseFare + service.price.addMarkup)) }}</span>
                      </div>
                    </span>
                  </span>
                  <div class="service-desc">
                    <span class="service-label">{{ service.desc }}</span>
                    <div class="pax-luggage">
                      <div :class="{'item':true, 'disabled':passenger > service.maxPaxVal}"
                        :title="$t('rsvpDetails.tripInformation.passenger')">
                        <i class="fas fa-users"></i>
                        <span class="rule-value">{{ service.maxPaxVal }}</span>
                      </div>
                      <div :class="{'item':true, 'disabled':luggage > service.maxLuggageVal}"
                        :title="$t('rsvpDetails.tripInformation.luggage')">
                        <i class="fas fa-briefcase"></i>
                        <span class="rule-value">{{ service.maxLuggageVal }}</span>
                      </div>
                    </div>
                  </div>
                </label>
              </div>
              <div v-else>
                <label v-for="(service, index) in servicePrices"
                  @click="selectService(serviceDetails(index).code, service)"
                  :class="{'radio pricing':true, 'active':selectedService === serviceDetails(index).code, 'disabled':serviceDisable(index, service) || service.status !== 'allowed' || service.unavailableService}">
                  <span v-if="service.unavailableService" class="unavailable">
                    <span class="availability">
                      <span class="available">Not Available</span>
                    </span>
                  </span>
                  <span v-else-if="!serviceAvailability(service).enabled || !(serviceAvailability(service).enabled && !serviceAvailability(service).eligible)">
                    <span :class="{'value':true, 'has-discount':price(service).hasDiscount, 'price-override':priceOverrideEnabled && !!priceOverride}">
                      <span v-if="priceOverrideEnabled">{{ `${currencyCode !== 'USD' ? price(service).value : price(service).original}` }}</span>
                      <span v-else>{{ price(service).value }}</span>
                      <div v-show="price(service).hasDiscount">
                        <span>{{ price(service).original }}</span>
                      </div>
                    </span>
                  </span>
                  <span v-else class="unavailable">
                    <span class="availability">
                      <span v-if="serviceAvailability(service).unavailable" class="available">
                        Not Available
                      </span>
                      <span v-else class="available">
                        <span v-if="serviceAvailability(service).after">
                          <span class="available">Available from</span>
                          <span class="date-time">{{serviceAvailability(service).after}}</span>
                        </span>
                        <span v-else>
                          <span class="available">Available until</span>
                          <span class="date-time">{{serviceAvailability(service).before}}</span>
                        </span>
                      </span>
                    </span>
                  </span>
                  <div class="service-desc">
                    <span class="service-label">{{ serviceDetails(index).label }}</span>
                    <div class="pax-luggage">
                      <div :class="{'item':true, 'disabled':maxPax[index]}"
                        :title="$t('rsvpDetails.tripInformation.passenger')">
                        <i class="fas fa-users"></i>
                        <span class="rule-value">{{ service.maxPassengers }}</span>
                      </div>
                      <div :class="{'item':true, 'disabled':maxLuggage[index]}"
                        :title="$t('rsvpDetails.tripInformation.luggage')">
                        <i class="fas fa-briefcase"></i>
                        <span class="rule-value">{{ service.maxLuggage }}</span>
                      </div>
                    </div>
                  </div>
                </label>
              </div>
            </div>
          </div>
        </div>

        <div :class="['row', {'showDesktop':isDesktop, 'hideMobile':isMobile}]" v-if="airportPickup && hasPickupType">
          <div class="medium-6 small-12 columns end" v-show="showCurbside && !forceMeetandGreet">
            <div class="data-field">
              <div class="radio label-only">
                <span class="pointer pickuptype-label" @click="isMeetAndGreet = false">
                  <i :class="{'far fa-circle fa-lg radio-fa':true, 'checked':!isMeetAndGreet}"></i>
                  {{ $t('rsvpDetails.edit.curbside') }}
                </span>
              </div>
            </div>
          </div>
          <div class="medium-6 small-12 columns end" v-show="pickupTypeData.meetAndGreet || forceMeetandGreet">
            <div class="data-field">
              <div class="radio label-only">
                <span class="pointer pickuptype-label" @click="isMeetAndGreet = true">
                  <i :class="{'far fa-circle fa-lg radio-fa':true, 'checked':isMeetAndGreet}"></i>
                  {{ $t('rsvpDetails.edit.meetAndGreet') }}
                  (
                  <b v-if="priceOverrideEnabled && !!priceOverride">{{ applyCurrency(0) }}</b>
                  <b v-else-if="pickupTypeData.pickupFee">{{ applyCurrency(toFixed(pickupTypeData.pickupFee)) }}</b>
                  <b v-else>{{ $t('rsvpDetails.edit.complimentary') }}</b>)
                </span>
              </div>
            </div>
          </div>
        </div>

        <div class="row optin-box" v-if="!mtech.enabled && _edit.selectedService && Object.keys(_edit.services).length && _edit.services[_edit.selectedService] && _edit.services[_edit.selectedService].optinAllow && optinSharedAllowedTime() && !pickupAsap && !extraStops.length && bookingType === 'mileage'">
          <label class="optin-toggle">
            <input type="checkbox" v-model="optinEnabled" />
            I want to share my ride with someone else
          </label>
          <div :class="['optin-desc', {active: optinEnabled}]">
            <div class="availability">
              <div v-show="editData.tripInfo.isAirportPickup">
                <i>
                  Available only from
                  <b>{{settings.optinAllowedTime.start}} - {{settings.optinAllowedTime.end}}</b>
                </i>
              </div>
            </div>
            <div class="opt-copy">
              <div class="head">Save {{_edit.services[_edit.selectedService].optinPercent}}% by sharing this ride.</div>
              If we find atleast 1 other person to share this ride with, we will refund you <b>{{ applyCurrency(optinRefundPrice().toFixed(2)) }}</b> 
              at the time your trip is completed.
            </div>
            <div class="flex" v-if="false">
              <div class="info">
                <div class="service-name">{{_edit.services[_edit.selectedService].name}}</div>
                <div class="capacity">
                  <i class="fas fa-users"></i>
                  {{_edit.services[_edit.selectedService].maxPassengers}}
                  <i class="fas fa-briefcase"></i>
                  {{_edit.services[_edit.selectedService].maxLuggage}}
                </div>
              </div>
              <div class="price">
                <span>{{ applyCurrency(optinSharedPrice().toFixed(2)) }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="small-12 columns">
            <div class="data-field">
              <div class="label">{{ $t('rsvpDetails.tripInformation.tip') }}</div>
              <div class="currency">
                <input type="text" :disabled="!isNewRsvp || (isNewRsvp && driverOverrideEnabled)" v-model="tipIntnl" v-if="currencyCode !== 'USD'"
                  :class="['input', {'has-error':!!validation.tip}]" ref="tip"
                  v-on:keypress="isNumber" v-on:keyup="forceManualTip" />
                <input type="text" :disabled="!isNewRsvp || (isNewRsvp && driverOverrideEnabled)" v-model="tripInfo.tip" v-else
                  :class="{'input':true, 'has-error':!!validation.tip}" ref="tip"
                  v-on:keypress="isNumber" v-on:keyup="forceManualTip" />
                <span>{{ currency.symbol }}</span>
              </div>
              <span class="error-msg">{{ validation.tip }}</span>
            </div>
          </div>
        </div>

        <div class="row" v-if="!mtech.enabled">
          <div class="small-12 columns">
            <div class="data-field">
              <div :class="{'custom-check':true, 'enabled':priceOverrideEnabled && priceOverrideAccess}"
                @click="priceOverrideToggle">
                <div><i class="fa fa-check"></i></div>
              </div>
              <span class="label" @click="priceOverrideToggle">
                {{ $t('rsvpDetails.edit.priceOverride.label') }}
                <small v-if="!priceOverrideAccess">(Disabled)</small>
              </span>
              <div class="currency">
                <input type="text" :disabled="!priceOverrideEnabled || !priceOverrideAccess" v-model="priceOverride"
                  :class="{'input':true, 'has-error':!!validation.priceOverride}" ref="priceOverride"
                  v-if="currencyCode === 'USD'"
                  />
                <input type="text" :disabled="!priceOverrideEnabled || !priceOverrideAccess" v-model="priceOverrideIntnl"
                  :class="['input', {'has-error':!!validation.priceOverride}]" ref="priceOverride"
                  v-else
                  />
                <span>{{ currency.symbol }}</span>
              </div>
              <span class="error-msg">{{ validation.priceOverride }}</span>
              <span class="description">{{ $t('rsvpDetails.edit.priceOverride.desc') }}</span>
            </div>
          </div>
        </div>

        <!-- <div class="row">
          <div class="small-12 columns">
            <div class="data-field">
              <div :class="{'custom-check':true, 'enabled':driverOverrideEnabled && overrideAccess}"
                @click="driverOverrideToggle">
                <div><i class="fa fa-check"></i></div>
              </div>
              <span class="label" @click="driverOverrideToggle">
                {{ $t('rsvpDetails.edit.driverOverride.label') }}
                <small v-if="!overrideAccess">(Disabled)</small>
              </span>
              <div class="currency">
                <input type="text" :disabled="!driverOverrideEnabled || !overrideAccess" v-model="driverOverride"
                  :class="{'input':true, 'has-error':!!validation.driverOverride}" ref="driverOverride" />
                <span>$</span>
              </div>
              <span class="error-msg">{{ validation.driverOverride }}</span>
              <span class="description">{{ $t('rsvpDetails.edit.driverOverride.desc') }}</span>
            </div>
          </div>
        </div> -->

        <div class="row" v-if="!isNewRsvp">
          <div class="small-6 columns">
            <div class="row">
              <div class="data-field">
                <div :class="{'custom-check':true, 'enabled':paymentBonusEnabled && overrideAccess}"
                  @click="paymentBonusToggle">
                  <div><i class="fa fa-check"></i></div>
                </div>
                <span class="label" @click="paymentBonusToggle">Driver Payment Bonus
                  <small v-if="!overrideAccess">(Disabled)</small>
                </span>
                <div class="currency">
                  <input type="text" :disabled="!paymentBonusEnabled || !overrideAccess" v-model="paymentBonus"
                    :class="{'input':true, 'has-error':!!validation.paymentBonus}" ref="paymentBonus" />
                  <span>$</span>
                  <!-- <span>{{ currency.symbol }}</span> -->
                </div>
                <span class="error-msg" v-if="!!validation.paymentBonus" style="margin-bottom: 5px;">{{ validation.paymentBonus }}</span>
                <span class="description">Driver payment bonus is only up to {{paymentBonusComputation.bonusLimitPercent}}% of fare (${{ toFixed(paymentBonusComputation.limitAmount) }})</span>
              </div>
            </div>
          </div>
          <div class="small-6 columns">
            <div class="row" v-if="overrideAccess && paymentBonusEnabled">
              <div class="small-6 columns">
                <div class="data-field">
                  <span class="label">
                    Original
                  </span>
                  <div class="currency">
                    <span class="amount">${{ paymentBonusComputation.original }}</span>
                    <!-- <span class="amount">{{ applyCurrency(paymentBonusComputation.original) }}</span> -->
                  </div>
                </div>
              </div>
                <div class="small-6 columns">
                  <div class="data-field">
                    <span class="label">
                      Total
                    </span>
                    <div class="currency">
                      <span class="amount">${{ paymentBonusComputation.updated }}</span>
                      <!-- <span class="amount">{{ applyCurrency(paymentBonusComputation.updated) }}</span> -->
                    </div>
                  </div>
                </div>
            </div>
          </div>
        </div>

        <div class="payment-summary" v-if="mtech.enabled">
          <div class="row">
            <div class="small-12 columns">
              <span class="title">{{ $t('rsvpDetails.edit.paymentSummary') }}</span>
            </div>
            <div class="small-12 columns">
              <div class="stats">
                <span class="label">{{ $t('rsvpDetails.edit.fare') }}</span>
                <span :class="{'value':true, 'price-override':priceOverrideEnabled && !!priceOverride}">
                  {{ applyCurrency(toFixed(paymentSummaryMtech.fare)) }}
                </span>
              </div>
            </div>
            <div class="small-12 columns">
              <div class="stats">
                <span class="label">{{ $t('rsvpDetails.edit.tip') }}</span>
                <span class="value">{{ applyCurrency(toFixed(paymentSummaryMtech.tip)) }}</span>
              </div>
            </div>
            <div class="small-12 columns" v-show="!!paymentSummaryMtech.tax">
              <div class="stats">
                <span class="label">Tax & Fees</span>
                <span :class="['value', {'price-override':priceOverrideEnabled && !!priceOverride}]">
                  {{ applyCurrency(toFixed(paymentSummaryMtech.tax)) }}
                </span>
              </div>
            </div>
            <div class="small-12 columns" v-show="!!paymentSummaryMtech.gratuity">
              <div class="stats">
                <span class="label">Service Gratuity</span>
                <span class="value">{{ applyCurrency(toFixed(Math.abs(paymentSummaryMtech.gratuity))) }}</span>
              </div>
            </div>
            <div class="small-12 columns" v-show="!!paymentSummaryMtech.discount">
              <div class="stats">
                <span class="label">Discount</span>
                <span class="value">-{{ applyCurrency(toFixed(Math.abs(paymentSummaryMtech.discount))) }}</span>
              </div>
            </div>
            <div class="small-12 columns">
              <div class="stats hard-border">
                <span class="label">{{ $t('rsvpDetails.edit.creditsUsed') }}</span>
                <span class="value">
                  <span v-if="paymentSummaryMtech.credits !== 0">-</span>
                  {{ applyCurrency(toFixed(paymentSummaryMtech.credits)) }}
                </span>
              </div>
            </div>
            <div class="small-12 columns">
              <div class="stats">
                <span class="label">{{ $t('rsvpDetails.edit.total') }}</span>
                <span class="value total">{{ applyCurrency(toFixed(paymentSummaryMtech.total)) }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="payment-summary" v-else>
          <div class="row">
            <div class="small-12 columns">
              <span class="title">{{ $t('rsvpDetails.edit.paymentSummary') }}</span>
            </div>
            <div class="small-12 columns">
              <div class="stats">
                <span class="label">{{ $t('rsvpDetails.edit.fare') }}</span>
                <span :class="['value', {'price-override':priceOverrideEnabled && !!priceOverride}]">
                  <span v-if="priceOverrideEnabled && !!priceOverride && currencyCode !== 'USD' && !waivePayments">
                    <!-- {{ currency.symbol }}{{ toFixed(priceOverrideIntnl || 0) }} -->
                    {{ xcurrency.format({ code: currency.code, val: toFixed(priceOverrideIntnl || 0) }) }}
                  </span>
                  <span v-else>{{ applyCurrency(toFixed(paymentSummaryData.fare)) }}</span>
                </span>
              </div>
            </div>

            <div class="small-12 columns" v-if="paymentSummaryData.portalFee">
              <div class="stats">
                <span class="label">Convenience Fee</span>
                <span :class="['value', {'price-override':priceOverrideEnabled && !!priceOverride}]">
                  <span v-if="priceOverrideEnabled && !!priceOverride && currencyCode !== 'USD' && !waivePayments">
                    {{ xcurrency.format({ code: currency.code, val: toFixed(0) }) }}
                  </span>
                  <span v-else>{{ applyCurrency(toFixed(paymentSummaryData.portalFee)) }}</span>
                </span>
              </div>
            </div>

            <div class="small-12 columns" v-show="paymentSummaryData.travelInsurance !== null && paymentSummaryData.travelInsurance > 0">
              <div class="stats">
                <span class="label">{{ travelInsuranceLabel }}</span>
                <span :class="{'value':true, 'price-override':priceOverrideEnabled && !!priceOverride}">
                  {{ applyCurrency(toFixed(paymentSummaryData.travelInsurance)) }}
                </span>
              </div>
            </div>
            <div class="small-12 columns"
              v-show="isMeetAndGreet && airportPickup">
              <div class="stats">
                <span class="label">{{ $t('rsvpDetails.edit.meetAndGreet') }}</span>
                <span :class="{'value':true, 'price-override':priceOverrideEnabled && !!priceOverride}"
                  v-if="paymentSummaryData.meetAndGreet !== $t('rsvpDetails.edit.complimentary')">
                  {{ applyCurrency(toFixed(paymentSummaryData.meetAndGreet)) }}
                </span>
                <span class="value" v-else>{{ paymentSummaryData.meetAndGreet }}</span>
              </div>
            </div>
            <div class="small-12 columns" v-show="paymentSummaryData.airportFee !== null && paymentSummaryData.airportFee > 0">
              <div class="stats">
                <span class="label">{{ $t('rsvpDetails.edit.airportFee') }}</span>
                <span class="value">{{ applyCurrency(toFixed(paymentSummaryData.airportFee)) }}</span>
              </div>
            </div>
            <div class="small-12 columns" v-show="paymentSummaryData.gasFee !== null && paymentSummaryData.gasFee > 0">
              <div class="stats">
                <span class="label">Fuel Surcharge</span>
                <span class="value">{{ applyCurrency(toFixed(paymentSummaryData.gasFee)) }}</span>
              </div>
            </div>
            <div class="small-12 columns">
              <div class="stats">
                <span class="label">{{ $t('rsvpDetails.edit.tip') }}</span>
                <span class="value" v-if="currencyCode === 'USD'">
                  {{ applyCurrency(toFixed(paymentSummaryData.tip)) }}
                </span>
                <span class="value" v-else>
                  <!-- {{ currency.symbol }}{{ toFixed(tipIntnl || 0) }} -->
                  {{ xcurrency.format({ code: currency.code, val: toFixed(tipIntnl || 0) }) }}
                </span>
              </div>
            </div>
            <div class="small-12 columns">
              <div class="stats hard-border">
                <span class="label">{{ $t('rsvpDetails.edit.creditsUsed') }}</span>
                <span class="value">
                  <span v-if="paymentSummaryData.credits !== 0">-</span>
                  {{ applyCurrency(toFixed(paymentSummaryData.credits)) }}
                </span>
              </div>
            </div>
            <div class="small-12 columns">
              <div class="stats">
                <span class="label">{{ $t('rsvpDetails.edit.total') }}</span>
                <span class="value total" v-if="currencyCode === 'USD'">{{ applyCurrency(toFixed(paymentSummaryData.total)) }}</span>
                <span class="value total" v-else>
                  {{ xcurrency.format({ code: currency.code, val: toFixed(paymentSummaryData.totalIntnl) }) }}
                  <!-- {{ currency.symbol }}{{ toFixed(paymentSummaryData.totalIntnl) }} -->
                </span>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
/* global CONFIG */
import US from 'us'
// import Tribute from 'tributejs'
import moment from 'moment-timezone'
import datepicker from 'vuejs-datepicker'
import location from '../Partials/LocationInput'
import flight from '../Partials/FlightInput'
import ApiServices from '@/modules/api/services'
import ApiPayment from '@/modules/api/payment'
import ApiRsvpEdit from '@/modules/api/reservation-details/edit'
import ApiRsvpAdd from '@/modules/api/reservation-details/add'
import ApiLocation from '@/modules/api/location'
import featureToggle from '@/modules/api/featureToggle'
import currencySymbol from '@/data/currency.js'
import xcurrency from '@/modules/api/currency'
import stripeForm from './StripeForm'

const countryData = require('@/data/countries')

let phoneUtil = require('google-libphonenumber').PhoneNumberUtil.getInstance()

export default {
  name: 'edit-rsvp',
  components: { datepicker, location, flight, stripeForm },
  data () {
    return {
      isMobile: false,
      isDesktop: false,
      isCustomPickupLocation: true,
      customPickupLocation: '',
      customPickupForceValid: true,
      pickupAirportId: 0,
      isCustomDropoffLocation: true,
      customDropoffLocation: '',
      customDropoffForceValid: true,
      dropoffAirportId: 0,
      wheelchairEnabled: false,
      serviceDogEnabled: false,
      waivePayments: false,
      priceOverrideEnabled: false,
      priceOverride: '',
      driverOverrideEnabled: false,
      driverOverride: '',
      paymentBonusEnabled: false,
      paymentBonus: '',
      bookingForSomeone: false,
      quoteDelay: '',
      selectedService: '',
      disableAllService: false,
      airlineLoading: false,
      isMeetAndGreet: false,
      forceMeetandGreet: false,
      today: moment(),
      forceDisableCurbside: false,
      validation: {
        pickupLocation: '',
        pickupAirport: '',
        dropoffLocation: '',
        dropoffAirport: '',
        pickupTime: '',
        airline: '',
        flight: '',
        guestFirstName: '',
        guestLastName: '',
        service: '',
        tip: '',
        priceOverride: '',
        driverOverride: '',
        paymentBonus: '',
        userSelected: '',
        creditCard: '',
        userFirstName: '',
        userLastName: '',
        userEmail: '',
        userMobile: '',
        userCardOwner: '',
        userCreditCard: '',
        userCardExp: '',
        userCvv: '',
        userAddress: '',
        userCity: '',
        userState: '',
        userZip: '',
        userCountry: '',
        userNotes: '',
        userNotesStatus: ''
      },
      maxHour: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
      pickupAsap: false,
      showSelectUser: false,
      countries: require('country-data').countries,
      createUser: {
        show: false,
        newCard: false,
        firstName: '',
        lastName: '',
        email: '',
        mobile: '',
        paymentMethod: 'newCard',
        cardOwner: '',
        creditCard: '',
        cardExp: '',
        cvv: '',
        address: '',
        city: '',
        state: '..',
        zip: '',
        country: '',
        notes: '',
        notesMaxChar: 500,
        noteStatus: '',
        selectedCard: ''
      },
      currentGeofence: '',
      driverOverrideValidation: {
        pickupAirport: true,
        dropoffAirport: true,
        selectedService: true,
        pickup: true,
        dropoff: true,
        servicePickup: true,
        serviceDropoff: true,
        selectAirportPickup: true,
        selectAirportDropoff: true
      },
      sendEmailReminder: false,
      bookingType: 'mileage',
      charterValidation: {
        startDate: '',
        endDate: ''
      },
      timezoneInfo: {
        pickup: '',
        tz: ''
      },
      currencySymbol: currencySymbol,
      tipIntnl: '',
      priceOverrideIntnl: '',
      xcurrency: xcurrency,
      d2dUser: {
        birthday: '',
        passport: '',
        address: '',
        city: '',
        zip: '',
        country: '',
        loading: false
      },
      d2dValidation: {
        birthday: '',
        address: '',
        city: '',
        zip: '',
        country: ''
      },
      countryList: Object.values(countryData)
    }
  },
  computed: {
    rsvpId () { return this.$route.params.id },
    travelInsuranceLabel () { return this.$settings.get('TravelInsuranceLabel') || 'Rider Fee' },
    brands () { return this.$store.state.brands.list },
    brandsLoading () { return this.$store.state.brands.loading },
    _edit () { return this.$store.state.rsvpDetails.edit },
    editData () { return this.$store.state.rsvpDetails.edit.details },
    tripInfo () { return this.editData.tripInfo },
    customerInfo () { return this.editData.customerInfo },
    guestInfo () { return this.editData.guestInfo },
    pickupExtra () {
      return {
        field: 'pickup',
        disabled: !this.isCustomPickupLocation || this.waivePayments,
        brandId: this.editData.tripInfo.brandId,
        error: this.validation.pickupLocation
      }
    },
    dropoffExtra () {
      return {
        field: 'dropoff',
        disabled: !this.isCustomDropoffLocation || this.waivePayments,
        brandId: this.editData.tripInfo.brandId,
        error: this.validation.dropoffLocation
      }
    },
    airlinesExtra () {
      let geofenceId = this.tripInfo.pickup.geofenceId
      if (this.tripInfo.pickup.geofenceType !== 'airport' &&
        this.tripInfo.dropoff.geofenceType === 'airport') {
          geofenceId = this.tripInfo.dropoff.geofenceId
        }
      return {
        gid: 'g' + geofenceId,
        loading: this.airlineLoading,
        error: this.validation.airline
      }
    },
    flightExtra () {
      return {
        loading: this.airlineLoading,
        error: this.validation.flight
      }
    },
    pickupDate () {
      let datetime = moment(this.editData.tripInfo.pickupDate, 'MM/DD/YYYY ' + featureToggle.timeFormat(this, false))
      let hourFormat = this.$settings.get('MilitaryTimeBackoffice') === '1' ? 'HH' : 'hh'
      return {
        date: datetime.format('MM/DD/YYYY'),
        hour: datetime.format(hourFormat),
        minute: datetime.format('mm'),
        amPm: datetime.format('A')
      }
    },
    disabledDate () {
      if (this.today.format('MM') === '01') {
        return {
          to: new Date(
            this.today.subtract(1, 'months').format('YYYY'),
            this.today.format('MM'),
            this.today.format('DD')
          )
        }
      }

      return {
        to: new Date(
          this.today.format('YYYY'),
          this.today.subtract(1, 'months').format('MM'),
          this.today.format('DD')
        )
      }
    },
    creditCard () {
      return this.customerInfo.creditCard === '' || this.customerInfo.creditCard === null ?
        'N/A' : '**** ' + this.customerInfo.creditCard
    },
    cardIcon () { return ApiPayment.cardIcon(this.customerInfo.cardTitle) },
    airportPickup () { return this.tripInfo.isAirportPickup },
    airportDropoff () { return this.tripInfo.isAirportDropoff },
    airportList () { return this.$store.state.airports.list },
    serviceLoading () { return this.$store.state.rsvpDetails.edit.serviceLoading },
    servicePrices () { return this.$store.state.rsvpDetails.edit.services },
    maxPax () {
      let servicePrices = this.servicePrices
      if (!this.useOldQuatation) {
        let pax = {}
        for (let service in servicePrices) {
          let code = servicePrices[service].code
          if (code) {
            pax[code] = false
            if (this.tripInfo.passenger > servicePrices[service].maxPassengers) {
              pax[code] = true
              if (this.selectedService === code) { this.selectedService = '' }
            }
          }
        }
        return pax
      } else {
        let pax = {
          shared: false,
          opoli: false,
          black: false,
          suv: false,
          shuttle: false
        }
        let services = ['shared', 'opoli', 'black', 'suv', 'shuttle']
        for (let i = 0; i <5; i++) {
          if (this.tripInfo.passenger > servicePrices[services[i]].maxPassengers) {
            pax[services[i]] = true
            if (this.selectedService === services[i]) { this.selectedService = '' }
          }
        }
        return pax
      }
    },
    maxLuggage () {
      let servicePrices = this.servicePrices
      if (!this.useOldQuatation) {
        let luggage = {}
        for (let service in servicePrices) {
          let code = servicePrices[service].code
          if (code) {
            luggage[code] = false
            if (this.tripInfo.luggage > servicePrices[service].maxLuggage) {
              luggage[code] = true
              if (this.selectedService === code) { this.selectedService = '' }
            }
          }
        }
        return luggage
      } else {
        let luggage = {
          shared: false,
          opoli: false,
          black: false,
          suv: false,
          shuttle: false
        }
        let services = ['shared', 'opoli', 'black', 'suv', 'shuttle']
        for (let i = 0; i <5; i++) {
          if (this.tripInfo.luggage > servicePrices[services[i]].maxLuggage) {
            luggage[services[i]] = true
            if (this.selectedService === services[i]) { this.selectedService = '' }
          }
        }
        return luggage
      }
    },
    pickupTypeData () {
      if (!this.selectedService || !this.servicePrices[this.selectedService]) {
        return {
          curbside: false,
          meetAndGreet: false,
          pickupFee: 0
        }
      }

      let data = this.servicePrices[this.selectedService]

      if ((data.curbside && !data.meetAndGreet) ||
        (data.curbside && !this.isMeetAndGreet && data.meetAndGreet)
      ) {
        this.isMeetAndGreet = false
      }

      if (this.pickupAsap) {
        return {
          curbside: true,
          meetAndGreet: false,
          pickupFee: 0
        }
      }

      this.forceMeetandGreet = false
      let mobileNo = this.createUser.show ? this.createUser.mobile : this.customerInfo.mobile
      if (mobileNo) {
        let isUS = true
        if (this.customerInfo.countryCode) {
          if (this.customerInfo.countryCode !== 1) {
          isUS = false
        }
      } else {
        try {
          let mobile = phoneUtil.parse('+' + mobileNo)
          if (mobile['values_']['1'] !== 1) {
              isUS = false
              // this.forceDisableCurbside = true
              if (this.createUser.show && this.callingCode == 1) isUS = true
            } else {
              this.forceDisableCurbside = false
            }
          } catch (err) {
            if (mobileNo.length > 3) {
              console.log('Phone error', err, this.customerInfo)
            }
            isUS = false
            if (this.createUser.show && this.callingCode == 1) isUS = true
          }
        }

        if (!isUS) {
          this.isMeetAndGreet = true
          this.forceMeetandGreet = true

          let serviceCode = this.$store.state.rsvpDetails.edit.selectedService
          let services = this.$store.state.rsvpDetails.edit.services
          if (serviceCode && services[serviceCode].licenseType === 'tnc' || services[serviceCode].name.indexOf('Private Van') > -1) {
            this.isMeetAndGreet = false
            this.forceMeetandGreet = false
            return {
              curbside: true,
              meetAndGreet: false,
              pickupFee: 0
            }
          } else if (serviceCode && services[serviceCode].licenseType === 'tcp') {
            return {
              curbside: false,
              meetAndGreet: true,
              pickupFee: 0
            }
          }
        } else {
          return {
            curbside: data.curbside,
            meetAndGreet: data.meetAndGreet,
            pickupFee: data.pickupFee
          }
        }
      }

      return this.servicePrices[this.selectedService]
    },
    hasPickupType () { return this.pickupTypeData.curbside || this.pickupTypeData.meetAndGreet },
    showCurbside () {
      return !this.forceDisableCurbside && this.pickupTypeData.curbside
      // return !this.forceDisableCurbside && this.pickupTypeData.curbside &&
      //   (!this.pickupTypeData.meetAndGreet || (this.pickupTypeData.meetAndGreet && this.pickupTypeData.pickupFee !== 0))
    },
    paymentSummaryData () {
      let fare = 0
      let mng = 0
      let tip = parseFloat(this.tripInfo.tip)
      let pax = this.tripInfo.passenger || 1
      let travelInsurance = 0
      let portalFee = 0
      if (!this.servicePrices[this.selectedService]) {
        return {
          fare: 0,
          travelInsurance: 0,
          meetAndGreet: 0,
          airportFee: 0,
          gasFee: 0,
          portalFee: 0,
          tip: 0,
          credits: 0,
          total: 0,
          totalIntnl: 0
        }
      }
      if (!!this.selectedService && this.servicePrices[this.selectedService].prices) {
        fare = parseFloat(this.servicePrices[this.selectedService].prices[pax - 1].price)
        mng = this.servicePrices[this.selectedService].pickupFee || this.$t('rsvpDetails.edit.complimentary')
      }

      let gasFee = 0
      if (this.selectedService !== '') {
        travelInsurance = this.servicePrices[this.selectedService].travelInsurance
        gasFee = this.servicePrices[this.selectedService].gasFee || 0
        if (this.servicePrices[this.selectedService].portalFee) {
          portalFee = this.servicePrices[this.selectedService].portalFee
          if (this.servicePrices[this.selectedService].portalFeeType === '%') {
            portalFee = parseFloat((fare * (this.servicePrices[this.selectedService].portalFee / 100)).toFixed(2))
          }
        }
      }

      if (!this.isNewRsvp) {
        let oldData = this.$store.state.rsvpDetails.basicInfo.tripInfo
        let tripInfo = this.tripInfo
        let oldExtraStops = this.$store.state.rsvpDetails.extraStops
        let newExtraStops = this.$store.state.rsvpDetails.edit.details.tripInfo.extraStops
        let hasExtraStopChanges = false
        if (oldExtraStops.length) {
          if (oldExtraStops.length !== newExtraStops.length) {
            hasExtraStopChanges = true
          } else {
            oldExtraStops.forEach((stop, index) => {
              if (stop.lat !== newExtraStops[index].lat || stop.lng !== newExtraStops[index].lng) {
                hasExtraStopChanges = true
              }
            })
          }
        } else if (newExtraStops && newExtraStops.length) {
          hasExtraStopChanges = true
        }
        if ((
          (
            this.constructAdress(oldData.pickup) === this.constructAdress(tripInfo.pickup) ||
            (oldData.pickup.geofenceType === 'airport' && tripInfo.pickup.geofenceType === 'airport' && oldData.pickup.geofenceId === tripInfo.pickup.geofenceId)
          ) && (
            this.constructAdress(oldData.dropoff) === this.constructAdress(tripInfo.dropoff) ||
            (oldData.dropoff.geofenceType === 'airport' && tripInfo.dropoff.geofenceType === 'airport' && oldData.dropoff.geofenceId === tripInfo.dropoff.geofenceId)
          ) &&
          oldData.pickupDate.substring(0, 10) === tripInfo.pickupDate.substring(0, 10) &&
          oldData.passenger === tripInfo.passenger && oldData.serviceCode === this.selectedService &&
          !hasExtraStopChanges && !this.charter.enabled) ||
          (
            this.charter.enabled && 
            (this.constructAdress(oldData.pickup) === this.constructAdress(tripInfo.pickup) ||
            (oldData.pickup.geofenceType === 'airport' && oldData.pickup.geofenceId === tripInfo.pickup.geofenceId)) &&
            oldData.pickupDate === this.charter.startDate && 
            moment(this.charter.startDate, 'MM/DD/YYYY hh:mmA').add(oldData.eta, 'seconds').format('MM/DD/YYYY hh:mmA') === this.charter.endDate
          ) ||
          this.waivePayments
        ) {
          fare = oldData.fare
          travelInsurance = oldData.travelInsurance
          gasFee = oldData.gasFee
          portalFee = oldData.portalFee
        }
      } else {
        tip = parseFloat(this.editData.tripInfo.tip) || 0
      }

      // let pickupDate = moment(this.pickupDate.date, 'MM/DD/YYYY')
      // if (this.holidayData.date === pickupDate.format('YYYY-MM-DD')) {
      //   fare = this.holidayPricing({
      //     code: this.selectedService,
      //     price: parseInt(this.servicePrices[this.selectedService].prices[pax - 1].original)
      //   })
      // }

      let isUS = true
      let mobileNo = this.createUser.show ? this.createUser.mobile : this.customerInfo.mobile
      if (this.customerInfo.countryCode) {
        if (this.customerInfo.countryCode !== 1) {
          isUS = false
        }
      } else if (mobileNo) {
        try {
          let mobile = phoneUtil.parse('+' + mobileNo, '')
          if (mobile['values_']['1'] !== 1) {
            isUS = false
            if (this.createUser.show && this.callingCode == 1) isUS = true
          }
        } catch (err) {
          if (mobileNo.length > 3) {
            console.log('Phone error', err, this.customerInfo)
          }
          isUS = false
          if (this.createUser.show && this.callingCode == 1) isUS = true
        }
      }
      if (!isUS) {
        mng = 0

        let serviceCode = this.$store.state.rsvpDetails.edit.selectedService
        let services = this.$store.state.rsvpDetails.edit.services
        if (this.selectedService && this.servicePrices[this.selectedService].licenseType === 'tnc' ||
          this.servicePrices[this.selectedService].name.indexOf('Private Van') > -1
        ) {
          this.isMeetAndGreet = false
        } else if (serviceCode && services[serviceCode].licenseType === 'tcp') {
          this.isMeetAndGreet = true
        }
      }

      if (this.pickupAsap) {
        this.isMeetAndGreet = false
      }

      if (this.priceOverrideEnabled && !!this.priceOverride) {
        if (!this.tripInfo.optinShared || !this.tripInfo.optinShared.isOptinShared) {
          fare = parseFloat(this.priceOverride)
          mng = 0
        }
        if (this.servicePrices[this.selectedService].portalFee && this.servicePrices[this.selectedService].portalFeeType === '%') {
          portalFee = parseFloat((fare * (this.servicePrices[this.selectedService].portalFee / 100)).toFixed(2))
        }
      }

      let airportFee = 0
      if (this.selectedService !== '') {
        airportFee = this.servicePrices[this.selectedService].airportFee
      }

      let total = fare + tip + airportFee + travelInsurance + gasFee + portalFee
      if (mng !== this.$t('rsvpDetails.edit.complimentary') && this.isMeetAndGreet && this.airportPickup) {
        total = total + mng
      }
      total = total - (this.tripInfo.credits || 0)
      total = total < 0 ? 0 : total

      let totalIntnl = 0
      if (this.currencyCode !== 'USD') {
        // totalIntnl = total - tip
        totalIntnl += parseFloat((airportFee * this.currency.value).toFixed(2))
        totalIntnl += parseFloat((travelInsurance * this.currency.value).toFixed(2))
        totalIntnl += parseFloat(((gasFee || 0) * this.currency.value).toFixed(2))
        totalIntnl += parseFloat((portalFee * this.currency.value).toFixed(2))
        if (mng !== this.$t('rsvpDetails.edit.complimentary') && this.isMeetAndGreet && this.airportPickup) {
          totalIntnl += parseFloat((mng * this.currency.value).toFixed(2))
        }
        if (this.priceOverrideEnabled && !!this.priceOverride) {
          totalIntnl += parseFloat(this.priceOverrideIntnl || 0)
        } else {
          totalIntnl += fare * this.currency.value
        }
        totalIntnl += parseFloat(this.tipIntnl || 0)
        totalIntnl -= this.tripInfo.credits * this.currency.value
        // totalIntnl = (totalIntnl * this.currency.value)
      }

      return {
        fare: fare,
        travelInsurance: travelInsurance,
        meetAndGreet: mng,
        airportFee: airportFee,
        gasFee: gasFee,
        portalFee: portalFee,
        tip: this.tripInfo.tip || 0,
        credits: this.tripInfo.credits || 0,
        total: total || 0,
        totalIntnl: totalIntnl
      }
    },
    paymentBonusComputation() {
      let bonusLimitPercent = this.$settings.get('ProviderPaymentBonusLimit')
      let limitAmount = 0
      let fare = this.paymentSummaryData.fare
      let basePay = this.tripInfo.basePay != 0 ? this.tripInfo.basePay : this.tripInfo.driverPayment
      let original = basePay + this.tripInfo.driverBonus
      let updated = original

      if (this.paymentBonusEnabled ) {
        let bonus = parseFloat(this.paymentBonus) || 0
        updated = updated + bonus
      }

      limitAmount = (bonusLimitPercent/100) * fare
      return {
        bonusLimitPercent: bonusLimitPercent,
        limitAmount: limitAmount,
        original: original,
        updated: parseFloat(updated).toFixed(2)
      }
    },
    holidayData () { return this.$store.state.rsvpDetails.edit.holiday },
    isMilitaryTime () {
      if (this.$settings.get('MilitaryTimeBackoffice') === '1') {
        this.maxHour = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23]
      }
      return this.$settings.get('MilitaryTimeBackoffice') === '1'
    },
    isNewRsvp () { return this.$store.state.rsvpDetails.isAdd },
    passenger: {
      get: function () { return this.tripInfo.passenger || 1 },
      set: function (value) {
        this.$store.commit('rsvpDetails/setPassenger', value)
        if(this.originalPax !== value) {
          this.paymentBonusEnabled = false
          this.paymentBonus = 0
        }
        if (this.mtech.enabled && this.selectedService) {
          if (value > this.mtech.services[this.selectedService].maxPaxVal) {
            this.selectedService = ''
          }
        }
      }
    },
    luggage: {
      get: function () { return this.tripInfo.luggage || 1 },
      set: function (value) {
        this.$store.commit('rsvpDetails/setLuggage', value)
        if (this.mtech.enabled && this.selectedService) {
          if (value > this.mtech.services[this.selectedService].maxLuggageVal) {
            this.selectedService = ''
          }
        }
      }
    },
    states () {
      let states = []
      for (let key in US.states) {
        if (US.states.hasOwnProperty(key)) {
          states.push({
            value: key,
            label: US.states[key].name
          })
        }
      }
      return states
    },
    noteCounter () {
      let count =  this.createUser.notesMaxChar - this.createUser.notes.length
      return count < 0 ? 0 : count
    },
    userCardLoading () { return this.$store.state.rsvpDetails.userCard.loading },
    userCardlist () { return this.$store.state.rsvpDetails.userCard.list },
    asapBidId () { return this.$store.state.rsvpDetails.asap.bidId },
    priceOverrideAccess () { return this.$store.state.session.user.access.hasOwnProperty('PageAdminPriceOverride') },
    overrideAccess () { return this.$store.state.session.user.access.hasOwnProperty('PageAdminOverride') },
    hasWaivePaymentAccess () { return this.$store.state.session.user.access.hasOwnProperty('PageAdminRSVPWaivePayments') },
    useOldQuatation () { return CONFIG.useOldQuatation },
    isCorporate () { return this.$store.state.rsvpDetails.isCorporate },
    isFreshDeliveries () {
      if (this.editData.tripInfo && this.editData.tripInfo.brandId) {
        return CONFIG.brands.find(x => x.id === this.editData.tripInfo.brandId) &&
          CONFIG.brands.find(x => x.id === this.editData.tripInfo.brandId).code === 'fd'
      }
      return false
    },
    extraStops () { return this.tripInfo.extraStops },
    enableCharterBooking () {
      let enabledBrands = []
      if (this.$settings.get('CharterEnabledBrands')) enabledBrands = this.$settings.get('CharterEnabledBrands').split(',')
      return enabledBrands.findIndex(x => x == this.editData.tripInfo.brandId) > -1
    },
    charter () { return this.$store.state.rsvpDetails.charter },
    startDate () {
      let datetime = moment(this.charter.startDate, 'MM/DD/YYYY ' + featureToggle.timeFormat(this, false))
      let hourFormat = this.$settings.get('MilitaryTimeBackoffice') === '1' ? 'HH' : 'hh'
      return {
        date: datetime.format('MM/DD/YYYY'),
        hour: datetime.format(hourFormat),
        minute: datetime.format('mm'),
        amPm: datetime.format('A')
      }
    },
    endDate () {
      let datetime = moment(this.charter.endDate, 'MM/DD/YYYY ' + featureToggle.timeFormat(this, false))
      let hourFormat = this.$settings.get('MilitaryTimeBackoffice') === '1' ? 'HH' : 'hh'
      return {
        date: datetime.format('MM/DD/YYYY'),
        hour: datetime.format(hourFormat),
        minute: datetime.format('mm'),
        amPm: datetime.format('A')
      }
    },
    optin () { return this.$store.state.rsvpDetails.optin },
    optinEnabled: {
      get () { return this.optin.enabled },
      set (x) { this.$store.commit('rsvpDetails/setOptinData', {enabled: x}) }
    },
    settings () { return this.$store.state.settings },
    charterMaxDura () { return this.$store.state.settings.charterMaxDura },
    mtech () { return this.$store.state.rsvpDetails.mtech },
    flightDate () {
      let adminTimeBuffer = this.$store.state.settings.adminTimeBuffer
      let datetime = moment().add(adminTimeBuffer, 'minutes')
      if (this.mtech.flightDate) {
        datetime = moment(this.mtech.flightDate, 'MM/DD/YYYY ' + featureToggle.timeFormat(this, false))
      }
      let hourFormat = this.$settings.get('MilitaryTimeBackoffice') === '1' ? 'HH' : 'hh'
      return {
        date: datetime.format('MM/DD/YYYY'),
        hour: datetime.format(hourFormat),
        minute: datetime.format('mm'),
        amPm: datetime.format('A')
      }
    },
    paymentSummaryMtech () {
      let fare = 0
      let tax = 0
      let gratuity = 0
      let discount = 0
      let tip = parseFloat(this.tripInfo.tip)
      let pax = this.tripInfo.passenger || 1

      if (!this.selectedService) {
        return {
          fare: 0,
          tax: 0,
          discount: 0,
          tip: 0,
          credits: 0,
          total: 0
        }
      }

      let service = this.mtech.services[this.selectedService]
      service.price.breakdown.forEach(breakdown => {
        if (breakdown.description.toLowerCase() == 'base rate') {
          fare = parseFloat(breakdown.amount + service.price.addMarkup)
        }
        if (breakdown.description.toLowerCase() == 'tax & fees') {
          tax = parseFloat(breakdown.amount)
        }
        if (breakdown.description.toLowerCase() == 'service gratuity') {
          gratuity = parseFloat(breakdown.amount)
        }
        if (breakdown.description.toLowerCase() == 'discount') {
          discount = parseFloat(breakdown.amount)
        }
      })
      // if (service) {
      //   fare = parseFloat(service.price.breakdown[0].amount + service.price.addMarkup)
      //   tax = parseFloat(service.price.breakdown[1].amount || 0)
      //   if (service.price.breakdown.length === 3) {
      //     discount = parseFloat(service.price.breakdown[2].amount || 0)
      //   }
      // }
      
      tip = parseFloat(this.editData.tripInfo.tip) || 0

      let total = fare + tip + tax + gratuity + discount
      total = total - (this.tripInfo.credits || 0)
      total = total < 0 ? 0 : total

      return {
        fare: fare,
        tax: tax,
        gratuity: gratuity,
        discount: discount,
        tip: this.tripInfo.tip || 0,
        credits: this.tripInfo.credits || 0,
        total: total || 0
      }
    },
    isDomestic: {
      get () { return this.mtech.isDomestic },
      set (x) { this.$store.commit('rsvpDetails/setMtechData', {isDomestic: x}) }
    },
    callingCode () {
      return this.$store.state.countryCode.callingCode
    },
    currency () { return this.$store.state.rsvpDetails.edit.currency },
    currencyList () { return this.$store.state.currency.list },
    currencyCode: {
      get () { return this.currency.code },
      set (x) {
        this.$store.commit('rsvpDetails/setEditCurrency', {
          code: x,
          symbol: currencySymbol[x],
          value: this.currencyList[x]
        })
      }
    },
    isD2D () {
      if (this.editData.tripInfo && this.editData.tripInfo.brandId) {
        return CONFIG.brands.find(x => x.id === this.editData.tripInfo.brandId) &&
          CONFIG.brands.find(x => x.id === this.editData.tripInfo.brandId).code === 'd2d'
      }
      return false
    },
    stripeStore () { return this.$store.state.rsvpDetails.stripe },
    convenienceFee () {
      return this.paymentSummaryData.fare ? parseFloat((this.paymentSummaryData.fare * .1).toFixed(2)) : 0
    }
  },
  methods: {
    init () {
      let self = this
      self.cleanValidation()
      self.getAirlineList()
      self.$store.commit('rsvpDetails/setOptinData', {
        enabled: false,
        shared: []
      })
      if (!self.isNewRsvp && self.tripInfo.optinShared.isOptinShared) {
        self.$store.commit('rsvpDetails/setOptinData', {enabled: true})
      }
      self.priceOverrideIntnl = 0
      if (self.tripInfo.overridePrice) {
        self.priceOverrideIntnl = self.$store.state.rsvpDetails.currency.amount
      }
      self.tipIntnl = 0
      if (this.$store.state.rsvpDetails.currency.currency !== 'USD') {
        self.tipIntnl = this.$store.state.rsvpDetails.currency.tipAmount
      }
      self.$nextTick(() => {
        self.getQuote()
      })
    },
    calendarFormat (date) { return moment(date).format('MM/DD/YYYY') },
    leadingZero (n) {
      return n < 10 ? '0' + n : n
    },
    toFixed (n) { return parseFloat(n).toFixed(2) },
    price (data) {
      if (!Object.values(data).length || !this.chkCharterMinDuration(data.code)) {
        return { value: 'N/A', hasDiscount: false, original: 0 }
      }

      if (!this.isNewRsvp) {
        let oldData = this.$store.state.rsvpDetails.basicInfo.tripInfo
        if (Object.keys(data).length === 0 || typeof data === 'undefined' ||
          typeof oldData.pickup.title === 'undefined'
        ) {
          return {
            value: this.applyCurrency(0),
            hasDiscount: false,
            original: this.applyCurrency(0)
          }
        }
      }

      let pax = this.passenger
      if (pax > Object.keys(data.prices).length || this.disableAllService) {
        return {
          value: 'N/A',
          hasDiscount: false,
          original: 0
        }
      }

      if (this.priceOverrideEnabled && !!this.priceOverride) {
        if (!this.tripInfo.optinShared || !this.tripInfo.optinShared.isOptinShared) {
          return {
            value: `${this.currency.symbol}${this.priceOverrideIntnl}`,
            hasDiscount: false,
            original: this.applyCurrency(this.priceOverride)
          }
        }
      }

      let price = data.prices[pax - 1].price
      let tripInfo = this.tripInfo

      if (!this.isNewRsvp) {
        let rsvpDetails = this.$store.state.rsvpDetails
        let oldData = rsvpDetails.basicInfo.tripInfo
        let oldExtraStops = rsvpDetails.extraStops
        let newExtraStops = rsvpDetails.edit.details.tripInfo.extraStops
        let hasExtraStopChanges = false
        if (oldExtraStops.length) {
          if (oldExtraStops.length !== newExtraStops.length) {
            hasExtraStopChanges = true
          } else {
            oldExtraStops.forEach((stop, index) => {
              if (stop.lat !== newExtraStops[index].lat || stop.lng !== newExtraStops[index].lng) {
                hasExtraStopChanges = true
              }
            })
          }
        } else if (newExtraStops && newExtraStops.length) {
          hasExtraStopChanges = true
        }
        if (
          ((
            this.constructAdress(oldData.pickup) === this.constructAdress(tripInfo.pickup) ||
            (oldData.pickup.geofenceType === 'airport' && oldData.pickup.geofenceId === tripInfo.pickup.geofenceId)
          ) &&
          (
            this.constructAdress(oldData.dropoff) === this.constructAdress(tripInfo.dropoff) ||
            (oldData.dropoff.geofenceType === 'airport' && oldData.dropoff.geofenceId === tripInfo.dropoff.geofenceId)
          ) &&
          oldData.pickupDate.substring(0, 10) === tripInfo.pickupDate.substring(0, 10) &&
          oldData.passenger === tripInfo.passenger && oldData.serviceCode === data.code &&
          !hasExtraStopChanges && !this.charter.enabled) ||
          (
            this.charter.enabled && 
            (this.constructAdress(oldData.pickup) === this.constructAdress(tripInfo.pickup) ||
            (oldData.pickup.geofenceType === 'airport' && oldData.pickup.geofenceId === tripInfo.pickup.geofenceId)) &&
            oldData.pickupDate === this.charter.startDate && 
            moment(this.charter.startDate, 'MM/DD/YYYY hh:mmA').add(oldData.eta, 'seconds').format('MM/DD/YYYY hh:mmA') === this.charter.endDate
          ) ||
          this.waivePayments
        ) {
          price = oldData.fare
        }
      }

      let isHoliday = false
      let pickupDate = moment(this.pickupDate.date, 'MM/DD/YYYY')
      if (this.holidayData.date === pickupDate.format('YYYY-MM-DD') && !this.waivePayments) {
        isHoliday = true
        price = this.holidayPricing({
          code: data.code,
          price: data.prices[pax - 1].original
        })
      }

      return {
        value: this.applyCurrency(this.toFixed(price)),
        hasDiscount: data.prices[pax - 1].price !== data.prices[pax - 1].original && !isHoliday,
        original: this.applyCurrency(data.prices[pax - 1].original)
      }
    },
    holidayPricing (data) {
      data.price = parseInt(data.price)
      let currency = this.holidayData[data.code].currency
      let value = parseInt(this.holidayData[data.code].value)

      if (currency === '$') {
        data.price += value
      } else {
        data.price += data.price * (value/100)
      }

      return data.price
    },
    constructAdress (addr) {
      if (!addr) {
        return ''
      }
      return addr.address2 + addr.address1 + addr.city + addr.state + addr.zip
    },
    call (user) {
      let self = this
      let phone = `${user.countryCode}${user.nationalNumber || user.mobile}`
      self.$plugins.phone.startCall(self, {
        phone: parseInt(phone),
        name: user.firstName + ' ' + user.lastName
      })
    },
    getAirlineList () {
      let self = this

      let pickup = self.tripInfo.pickup
      let dropoff = self.tripInfo.dropoff
      let airlines = self.$store.state.airlines
      let geofenceId = pickup.geofenceId
      if (pickup.geofenceType !== 'airport' && dropoff.geofenceType === 'airport') {
        geofenceId = dropoff.geofenceId
      }
      if (this.charter.enabled) {
        geofenceId = this.charter.airportGeoId
      }
      if (airlines['g' + geofenceId] ||
        (typeof pickup.geofenceType === 'undefined' && dropoff.geofenceType !== 'airport' && !self.charter.enabled) ||
        (pickup.geofenceType !== 'airport' && dropoff.geofenceType !== 'airport' && !self.charter.enabled) ||
        (!self.airportPickup && self.charter.enabled)
      ) { return }

      if (self.airlineLoading || !geofenceId) { return }
      self.airlineLoading = true

      let sdkUrl = '/geofences/' + geofenceId + '/airlines'
      self.$sdk.get(sdkUrl, {}, res => {
        self.airlineLoading = false
        if (!res.error) {
          let airlines = []
          res.list.forEach(function (data) {
            airlines.push({
              code: data.code,
              name: data.name,
              label: '(' +data.code+ ') ' + data.name
            })
          })
          self.$store.commit('airlines/addAirlines', {
            id: 'g' + geofenceId,
            list: airlines
          })
        }
      })
    },
    selectAirportPickup (selected) {
      if (selected === 0) { return }
      let self = this
      let geofenceId = typeof selected === 'number' ? selected : parseInt(selected.target.value)
      let index = self.airportList.findIndex(x => x.geofenceId === geofenceId)
      self.$store.commit('rsvpDetails/setPickup', self.airportList[index])
      self.$store.commit('rsvpDetails/setIsAirportPickup', true)
      self.$store.commit('rsvpDetails/setAirportCode', self.airportList[index].airportCode)
      self.getAirlineList()

      self.$store.commit('rsvpDetails/setTimezoneLoading', true)
      ApiLocation.getTimezone(self, {
        lat: self.tripInfo.pickup.lat,
        lng: self.tripInfo.pickup.lng
      }, function (res) {
        self.timezoneInfo = {
          pickup: self.tripInfo.pickup.title,
          tz: res
        }
        self.$store.commit('rsvpDetails/setTimezoneLoading', false)
        self.$store.commit('rsvpDetails/setTimezone', res)
      })

      if (self.quoteDelay) { clearTimeout(self.quoteDelay) }
      self.quoteDelay = setTimeout(function () {
        self.getQuote()
      }, 800)

      // if (this.$store.state.rsvpDetails.basicInfo.tripInfo.pickup.geofenceId != selected.target.value) {
      if (this.$store.state.rsvpDetails.basicInfo.tripInfo.pickup.geofenceId != geofenceId) {
        this.driverOverrideValidation.selectAirportPickup = false
      } else  {
        this.driverOverrideValidation.selectAirportPickup = true
      }
      this.clearDriverPayment()
    },
    selectAirportDropoff (selected) {
      if (selected === 0) { return }
      let self = this
      let geofenceId = typeof selected === 'number' ? selected : parseInt(selected.target.value)
      let index = self.airportList.findIndex(x => x.geofenceId === geofenceId)
      this.$store.commit('rsvpDetails/setDropoff', self.airportList[index])
      self.$store.commit('rsvpDetails/setIsAirportDropoff', true)
      if (!self.airportPickup && self.airportDropoff) {
        self.$store.commit('rsvpDetails/setAirportCode', self.airportList[index].airportCode)
        self.getAirlineList()
      }

      if (self.quoteDelay) { clearTimeout(self.quoteDelay) }
      self.quoteDelay = setTimeout(function () {
        self.getQuote()
      }, 800)

      if (this.$store.state.rsvpDetails.basicInfo.tripInfo.dropoff.geofenceId != selected) {
        this.driverOverrideValidation.selectAirportDropoff = false
      } else  {
        this.driverOverrideValidation.selectAirportDropoff = true
      }
      this.clearDriverPayment()
    },
    serviceDisable (index, service) {
      if (this.charter.enabled && service.code) {
        if (!this.chkCharterMinDuration(service.code)) return true
      }

      if (this.serviceAvailability(service).enabled ? (this.serviceAvailability(service).eligible ? false : true) : false) {
        if (this.serviceAvailability(service).code === this.selectedService) {
          this.selectedService = ''
        }
      }
      let nowPlusBuffer = moment().add(service.bufferTime, 'minutes')
      let pickupDate = moment(this.tripInfo.pickupDate, 'MM/DD/YYYY hh:mmA')
      let serviceUnavailable = service.serviceUnavailableTime
      return this.serviceLoading || this.maxPax[index] ||
        this.maxLuggage[index] || !this.sharedAllowedTime(index) ||
        this.disableAllService
        || this.serviceAvailability(service).enabled ? (this.serviceAvailability(service).eligible ? false : true) : false ||
        (this.extraStops && this.extraStops.length > 0 && service.isShared) ||
        (service.bufferTime && pickupDate.isBefore(nowPlusBuffer))
    },
    chkCharterMinDuration (service) {
      if (!this.charter.enabled) return true
      let selectedService = service.replace('a', '')
      let minDuration = this.settings.charterMinDuration
      let minDurationServiceId = this.settings.charterMnDurationServiceId
      let startDate = moment(this.charter.startDate, 'MM/DD/YYYY hh:mmA')
      let endDate = moment(this.charter.endDate, 'MM/DD/YYYY hh:mmA')
      if (
        !this.charter.startDate || !this.charter.endDate ||
        (minDurationServiceId.indexOf(selectedService) > -1 && endDate.diff(startDate, 'minutes') < minDuration)
      ) return false // Time duration within charter-min-duration settings
      return true // NO ISSUE
    },
    serviceAvailability(service) {
      let availability = service.availability
      if(typeof availability === 'undefined' || availability === null) {
        return {
          enabled: false,
          eligible: false
        }
      }
      return {
        enabled: availability.enabled,
        eligible: availability.eligible > 0 ? true:false,
        unavailable: availability.enabled && availability.eligible === 0 && !!!availability.suggest.before && !!!availability.suggest.after,
        before: availability.suggest.before ? moment(availability.suggest.before, 'YYYY-MM-DD HH:mm').format('MM/DD/YYYY h:mmA') : '',
        after: availability.suggest.after ? moment(availability.suggest.after, 'YYYY-MM-DD HH:mm').format('MM/DD/YYYY h:mmA') : '',
        code: service.code
      }
    },
    sharedAllowedTime (service) {
      let sharedAllow = true
      if (this.editData.tripInfo.isAirportPickup && !this.isFreshDeliveries) {
        let selectedTime = moment(this.editData.tripInfo.pickupDate, 'MM/DD/YYYY ' + featureToggle.timeFormat(this, false))
        selectedTime = moment(selectedTime.format(featureToggle.timeFormat(this, false)), featureToggle.timeFormat(this, false))
        if (this.pickupAsap) {
          selectedTime = moment()
        }
        let settings = this.settings
        let allowTimeStart = moment(settings.sharedAllowedTime.start, 'hh:mmA')
        let allowTimeEnd = moment(settings.sharedAllowedTime.end, 'hh:mmA')
        if (!this.useOldQuatation) {
          if (this.$store.state.rsvpDetails.edit.services[service] &&
            this.$store.state.rsvpDetails.edit.services[service].isShared &&
            !selectedTime.isBetween(allowTimeStart, allowTimeEnd)
          ) {
            sharedAllow = false
            if (this.selectedService === service) {
              this.selectedService = ''
            }
          }
        } else {
          if (service === 'shared' && !selectedTime.isBetween(allowTimeStart, allowTimeEnd)) {
            sharedAllow = false
            if (this.selectedService === service) {
              this.selectedService = ''
            }
          }
        }
      }

      return sharedAllow
    },
    serviceDetails (index) {
      if (!this.useOldQuatation) {
        let _service = this.servicePrices[index]
        return {
          code: _service.code,
          label: _service.name
        }
      } else {
        switch (index) {
          case 'shared': return { code: 'shared', label: ApiServices.getServiceName(index) }
          case 'opoli': return { code: 'opoli', label: ApiServices.getServiceName(index) }
          case 'black': return { code: 'black', label: ApiServices.getServiceName(index) }
          case 'SUV': return { code: 'suv', label: ApiServices.getServiceName(index) }
          case 'suv': return { code: 'suv', label: ApiServices.getServiceName(index) }
          case 'shuttle': return { code: 'shuttle', label: ApiServices.getServiceName(index) }
          default: return {code: '', label: '...' }
        }
      }
    },
    selectService (code, service) {
      if (!this.sharedAllowedTime(code) || this.serviceDisable(code, service) || service.unavailableService) { return }
      this.selectedService = code
      // if (this.pickupTypeData.curbside && (!this.pickupTypeData.meetAndGreet ||
      //   (this.pickupTypeData.meetAndGreet && this.pickupTypeData.pickupFee === 0))
      // ) {
      //   this.isMeetAndGreet = true
      // }

      let mobileNo = this.createUser.show ? this.createUser.mobile : this.customerInfo.mobile
      if (mobileNo) {
        let data = this.servicePrices[this.selectedService]
        this.forceDisableCurbside = false

        if (data.curbside && data.meetAndGreet) {
          if (this.customerInfo.countryCode) {
            if (this.customerInfo.countryCode !== 1) {
              this.forceDisableCurbside = true
              this.isMeetAndGreet = true
            }
          } else{
            try {
              let mobile = phoneUtil.parse('+' + mobileNo, '')
              this.forceDisableCurbside = false
              if (mobile['values_']['1'] !== 1) {
                this.forceDisableCurbside = true
                this.isMeetAndGreet = true
              }
            } catch (err) {
              if (mobileNo.length > 3) {
                console.log('Phone error', err, this.customerInfo)
              }
              this.forceDisableCurbside = true
              this.isMeetAndGreet = true
            }
          }
        }
      }

      if (this.pickupAsap) {
        this.forceDisableCurbside = false
        this.isMeetAndGreet = false
      }

      if (this.paymentBonusEnabled) {
        this.paymentBonusEnabled = false
        this.paymentBonus = 0
      }

      //code
      if (this.selectedService != this.$store.state.rsvpDetails.basicInfo.tripInfo.serviceType) {
        this.driverOverrideValidation.selectedService = false
      } else {
        this.driverOverrideValidation.selectedService = true
      }
      this.clearDriverPayment()
      
      this.$store.commit('rsvpDetails/setOptinData', {enabled: false})
    },
    locationNewValue (data) {
      let self = this
      let field = data.field.charAt(0).toUpperCase() + data.field.slice(1)
      this['custom' + field + 'ForceValid'] = data.keyInput !== ''

      if (data.field === 'pickup' && data.keyInput === '') {
        this.customPickupLocation = ''
      } else if (data.field === 'dropoff' && data.keyInput === ''){
        this.customDropoffLocation = ''
      }

      if (!!data.keyInput === false) { return }
      if (self.quoteDelay) {
        clearTimeout(self.quoteDelay)
      }

      if (this.charter.enabled) {
        if (!this.charter.startDate || !this.charter.endDate) {
          return
        }
      }

      if (
        data.field === 'pickup' && !this._edit.timezoneLoading &&
        this.timezoneInfo.pickup !== this.tripInfo.pickup.title
      ) {
        self.getTimezoneFirst()
      } else {
        self.quoteDelay = setTimeout(function () {
          if (!CONFIG.useOldQuatation) {
            self.selectedService = ''
          }
          self.getQuote()
        }, 500)
      }


      //code
      if (data.field === 'pickup' && (data.keyInput != this.$store.state.rsvpDetails.basicInfo.tripInfo.pickup.title)) {
        this.driverOverrideValidation.pickup = false
        this.paymentBonusEnabled = false
        this.paymentBonus = 0
      } else {
        this.driverOverrideValidation.pickup = true
      }
      if (data.field === 'dropoff' && (data.keyInput != this.$store.state.rsvpDetails.basicInfo.tripInfo.dropoff.title)) {
        this.driverOverrideValidation.dropoff = false
        this.paymentBonusEnabled = false
        this.paymentBonus = 0
      } else {
        this.driverOverrideValidation.dropoff = true
      }
      this.clearDriverPayment()
    },
    pickupAsapToggle() {
      this.pickupAsap = !this.pickupAsap
      this.getQuote()
    },
    getQuote (useMtechPickupDate = false) {
      let self = this
      let asap = this.pickupAsap

      let rsvpEdit = self.$store.state.rsvpDetails.edit
      if (rsvpEdit.serviceLoading ||
        (self.isNewRsvp && (!self.tripInfo.pickup || (!self.tripInfo.dropoff && !self.charter.enabled)))
      ) { return }

      if (self.charter.enabled) {
        let startDate = moment(self.charter.startDate, 'MM/DD/YYYY hh:mmA')
        let endDate = moment(self.charter.endDate, 'MM/DD/YYYY hh:mmA')
        let hasError = false
        let adminTimeBuffer = this.$store.state.settings.adminTimeBuffer
        self.charterValidation.startDate = ''
        self.charterValidation.endDate = ''
        if (startDate.diff(moment().add(adminTimeBuffer, 'minutes')) <= 0) {
          self.charterValidation.startDate = this.$t('rsvpDetails.edit.adminTimeBufferError', {min: adminTimeBuffer})
          hasError = true
        }
        if (startDate.diff(endDate, 'minutes') > 0) {
          self.charterValidation.endDate = 'End date must be later than start date.'
          hasError = true
        }
        if (hasError) { return }
      }

      let pickupdate = moment()
      let pickuptime = moment()
      if (self.pickupDate.date && self.pickupDate.date !== 'Invalid date' && !self.pickupAsap) {
        pickupdate = moment(self.pickupDate.date, 'MM/DD/YYYY')
        pickuptime = moment(self.pickupDate.hour+':'+self.pickupDate.minute+' '+self.pickupDate.amPm, "h:mm A")
      }

      let quoteData = {}
      quoteData = {
        brandId: self.tripInfo.brandId,
        pickupDate: pickupdate.format('YYYY-MM-DD'),
        pickup: self.tripInfo.pickup,
        dropoff: self.tripInfo.dropoff,
        asap: asap,
        pickupTime: pickuptime.format("HH:mm:ss")
      }

      if (self.extraStops) {
        if (self.extraStops.length) {
          quoteData.extraStops = []
          self.extraStops.forEach(stop => {
            quoteData.extraStops.push({
              lat: stop.lat,
              lng: stop.lng
            })
          })
        }
      }

      self.$store.commit('rsvpDetails/setServiceLoading', true)
      if (self.charter.enabled) {
        let startDate = moment(self.charter.startDate, 'MM/DD/YYYY hh:mmA')
        let endDate = moment(self.charter.endDate, 'MM/DD/YYYY hh:mmA')
        let duration = moment.duration(endDate.diff(startDate))
        quoteData = {
          brandId: self.tripInfo.brandId,
          pickupDate: startDate.format('YYYY-MM-DD'),
          etaInSec: duration.asSeconds(),
          pickup:  self.tripInfo.pickup,
          isCharter: true
        }
      }

      ApiRsvpEdit.getQuote2(self, quoteData, function (res) {
        if (!self.$store.state.rsvpDetails.edit.selectedService) self.selectedService = ''
        self.$store.commit('rsvpDetails/setServiceLoading', false)
        self.disableAllService = false
        self.validation.service = ''

        if (res.error) {
          self.disableAllService = true
          self.selectedService = ''
          self.validation.service = self.$t('rsvpDetails.edit.serviceRestrictions')
          return
        }

        if (self.mtech.enabled && self.extraStops && self.extraStops.length) {
          self.selectedService = ''
          self.validation.service = self.$t('rsvpDetails.edit.serviceRestrictions')
        }

        let pickup = Object.assign({}, self.tripInfo.pickup)
        let dropoff = Object.assign({}, self.tripInfo.dropoff)
        if (self.charter.enabled) {
          pickup.geofenceId = res.geofence.id
          pickup.geofenceType = res.airport && res.airport.id ? 'airport' : res.geofence.type
          pickup.geofenceName = res.geofence.name
          self.$store.commit('rsvpDetails/setPickupGeofence', {
            id: pickup.geofenceId,
            name: pickup.geofenceName,
            type: pickup.geofenceType
          })
          self.$store.commit('rsvpDetails/setCharterData', {
            airportGeoId: res.airport && res.airport.id ? res.airport.id : ''
          })
          self.$store.commit('rsvpDetails/setPickup', pickup)
          self.$store.commit('rsvpDetails/setIsAirportPickup', res.airport && res.airport.id)
        } else {
          if (res.lyft) {
            pickup.geofenceId = ''
            pickup.geofenceType = ''
            pickup.geofenceName = ''
          } else {
            pickup.geofenceId = res.geofences.pickup.id
            pickup.geofenceType = res.geofences.pickup.type
            pickup.geofenceName = res.geofences.pickup.description
            self.$store.commit('rsvpDetails/setPickupGeofence', {
              id: res.geofences.pickup.id,
              name: res.geofences.pickup.description,
              type: res.geofences.pickup.type
            })
            dropoff.geofenceId = res.geofences.dropoff.id
            dropoff.geofenceType = res.geofences.dropoff.type
            dropoff.geofenceName = res.geofences.dropoff.description
            self.$store.commit('rsvpDetails/setDropoffGeofence', {
              id: res.geofences.dropoff.id,
              name: res.geofences.dropoff.description,
              type: res.geofences.dropoff.type
            })
          }
          self.$store.commit('rsvpDetails/setPickup', pickup)
          self.$store.commit('rsvpDetails/setDropoff', dropoff)

          if (!res.lyft) {
            self.$store.commit('rsvpDetails/setIsAirportPickup', res.geofences.pickup.type === 'airport')
            self.$store.commit('rsvpDetails/setIsAirportDropoff', res.geofences.dropoff.type === 'airport')
          }
        }

        if (res.lyft) {
          self.currentGeofence = ''
        } else {
          self.currentGeofence = res.geofence ? res.geofence.name : res.geofences.servicearea.name
        }
        // if (res.geofences.pickup.type === 'servicearea') {
        // } else if (res.geofences.dropoff.type === 'servicearea') {
        //   self.currentGeofence = res.geofences.dropoff.description
        // }

        if (useMtechPickupDate && self.mtech.enabled && res.mtech && res.mtech.services.length) {
          let pickupDate = moment(res.mtech.services[0].pickupTimes[0].unix, 'X')
          if (self.selectedService) {
            pickupDate = moment(self.mtech.services[self.selectedService].pickupTimes[0].unix, 'X')
          }
          self.$store.commit('rsvpDetails/setPickupDate', pickupDate.tz(self._edit.timezone).format('MM/DD/YYYY ' + featureToggle.timeFormat(self, false)))
        }

        if (self.mtech.enabled && self.selectedService) {
          self.getQuoteMtechService()
        }
      })

      if (!this._edit.timezoneLoading && this.timezoneInfo.pickup !== this.tripInfo.pickup.title) {
        self.$store.commit('rsvpDetails/setTimezoneLoading', true)
        ApiLocation.getTimezone(self, {
          lat: self.tripInfo.pickup.lat,
          lng: self.tripInfo.pickup.lng
        }, function (res) {
          self.timezoneInfo = {
            pickup: self.tripInfo.pickup.title,
            tz: res
          }
          self.$store.commit('rsvpDetails/setTimezoneLoading', false)
          self.$store.commit('rsvpDetails/setTimezone', res)
        })
      }
    },
    changeDate () {
      let self = this
      self.pickupDate.date = moment(self.pickupDate.date).format('MM/DD/YYYY')
      self.updatePickupDate()
      setTimeout(() => {
        self.getQuote()
      }, 20)
    },
    updatePickupDate () {
      let pickupDate = this.pickupDate.date + ' ' + this.pickupDate.hour + ':' +
        this.pickupDate.minute + '' + this.pickupDate.amPm
      if (this.$settings.get('MilitaryTimeBackoffice') === '1') {
        pickupDate = this.pickupDate.date + ' ' + this.pickupDate.hour + ':' + this.pickupDate.minute
      }
      this.$store.commit('rsvpDetails/setPickupDate', pickupDate)
      this.sharedAllowedTime(this.selectedService)
      setTimeout(() => {
        this.getQuote()
      }, 20)
    },
    priceOverrideToggle () {
      let self = this
      if (!self.priceOverrideAccess) { return }
      self.priceOverrideEnabled = !self.priceOverrideEnabled
      self.$nextTick(() => self.$refs.priceOverride.focus())
    },
    driverOverrideToggle () {
      let self = this
      self.driverOverrideEnabled = !self.driverOverrideEnabled
      if (self.driverOverrideEnabled) {
        self.$store.commit('rsvpDetails/setTip', 0)
      }
      self.$nextTick(() => self.$refs.driverOverride.focus())
    },
    paymentBonusToggle () {
      let self = this
      if (!self.overrideAccess) { return }
      self.paymentBonusEnabled = !self.paymentBonusEnabled
      if(self.paymentBonusEnabled == false){
        self.$store.commit('rsvpDetails/setpaymentBonus', 0)
        self.paymentBonus = 0
      }
      self.$nextTick(() => self.$refs.paymentBonus.focus())
    },
    save () {
      let self = this

      if (!self.validateFields()) { return }

      let newData = self.buildData()
      let oldData = this.$store.state.rsvpDetails.basicInfo.tripInfo
      let oldTotalAmount = oldData.tip + oldData.fare + oldData.pickupFee +
        oldData.airportFee + oldData.travelInsurance + oldData.gasFee + (oldData.portalFee || 0) - oldData.credits
      oldTotalAmount = oldTotalAmount < 0 ? 0 : oldTotalAmount
      if (oldTotalAmount % 1 > 0) {
        oldTotalAmount = parseFloat(oldTotalAmount.toFixed(2))
      }

      if (newData.payment.total !== oldTotalAmount && newData.payment.total > 0 && !self.waivePayments) {
        let xOldAmount =  self.applyCurrency(self.toFixed(oldTotalAmount))
        let origCurrency = this.$store.state.rsvpDetails.currency
        let xNewAmount = self.applyCurrency(self.toFixed(newData.payment.total))
        if (self.currencyCode !== 'USD') {
          xOldAmount = origCurrency.airportFee + origCurrency.amount + origCurrency.gasFee + origCurrency.pickupFee + origCurrency.tipAmount + origCurrency.travelInsurance - origCurrency.credit
          // xOldAmount = `${origCurrency.symbol}${xOldAmount}`
          xOldAmount = self.xcurrency.format({
            code: self.currency.code,
            val: self.toFixed(xOldAmount)
          })
          xNewAmount = self.xcurrency.format({
            code: self.currency.code,
            val: self.toFixed(self.paymentSummaryData.totalIntnl)
          })
        }
        let lowHigh = newData.payment.total > oldTotalAmount ? 'higher' : 'lower'
        let label = '<div class="text-left">' + this.$t('rsvpDetails.edit.priceChange') + '</div>'
        label += '<div class="price-difference">' + this.$t('rsvpDetails.edit.totalFareChange')
        label += '<span>' + xOldAmount + '</span>'
        label += '<i class="fas fa-angle-right"></i>'
        label += '<span class="' +lowHigh+ '">' + xNewAmount + '</span>'
        label += '</div>'
        self.$store.commit('reservationNotes/show', {
          rsvpId: self.tripInfo.id,
          action: 'editRsvp',
          label: label,
          actionData: newData
        })
      } else {
        self.$store.commit('rsvpDetails/setIsSaving', true)
        ApiRsvpEdit.updateRsvp(self, newData, function (res) {
          self.$store.commit('rsvpDetails/setIsSaving', false)

          let status = 'success'
          let msg = self.$t('rsvpDetails.edit.updateSuccessMsg', {id: newData.rsvpId})
          if (res.error) {
            msg = res.error.message
            if (res.error.code === 'GATE402001') {
              msg = 'Unable to update this reservation. Payment information cannot be processed.'
            }
            status = 'error'
          }
          self.$store.commit('rsvpDetails/setShowUpdateReturn', {
            show: true,
            message: msg,
            status: status
          })
        })
      }
    },
    buildData () {
      let pickup = this.tripInfo.pickup
      let dropoff = this.tripInfo.dropoff
      let timezone = this.$store.state.rsvpDetails.edit.timezone
      let pickupDate = moment.tz(this.tripInfo.pickupDate, 'MM/DD/YYYY ' + featureToggle.timeFormat(this, false), timezone)
      let selectedService = this.servicePrices[this.selectedService]
      let servicePrice = selectedService.prices[this.tripInfo.passenger - 1]
      let fare = parseFloat(servicePrice.price)
      let travelInsurance = selectedService.travelInsurance || 0
      let gasFee = selectedService.gasFee || 0
      let portalFee = 0
      if (moment(this.tripInfo.pickupDate, 'MM/DD/YYYY').format('YYYY-MM-DD') === this.holidayData.date) {
        fare = parseInt(this.holidayPricing({
          code: selectedService.code,
          price: servicePrice.original
        }))
      }

      if (selectedService.portalFee) {
        portalFee = selectedService.portalFee
        if (selectedService.portalFeeType === '%') portalFee = parseFloat((fare * (selectedService.portalFee / 100)).toFixed(2))
      }
      
      let originalRsvpData = this.$store.state.rsvpDetails.basicInfo.tripInfo
      let oldExtraStops = this.$store.state.rsvpDetails.extraStops
      let newExtraStops = this.tripInfo.extraStops
      let hasExtraStopChanges = false
      if (oldExtraStops.length) {
        if (oldExtraStops.length !== newExtraStops.length) {
          hasExtraStopChanges = true
        } else {
          oldExtraStops.forEach((stop, index) => {
            if (stop.lat !== newExtraStops[index].lat || stop.lng !== newExtraStops[index].lng) {
              hasExtraStopChanges = true
            }
          })
        }
      } else if (newExtraStops.length) {
        hasExtraStopChanges = true
      }
      if ((originalRsvpData.pickupDate.substring(0, 10) === this.tripInfo.pickupDate.substring(0, 10) &&
        (
          this.constructAdress(originalRsvpData.pickup) === this.constructAdress(this.tripInfo.pickup) ||
          (originalRsvpData.pickup.geofenceType === 'airport' && this.tripInfo.pickup.geofenceType === 'airport' && originalRsvpData.pickup.geofenceId === this.tripInfo.pickup.geofenceId)
        ) && (
          this.constructAdress(originalRsvpData.dropoff) === this.constructAdress(this.tripInfo.dropoff) ||
          (originalRsvpData.dropoff.geofenceType === 'airport' && this.tripInfo.dropoff.geofenceType === 'airport' && originalRsvpData.dropoff.geofenceId === this.tripInfo.dropoff.geofenceId)
        ) &&
        // this.constructAdress(originalRsvpData.pickup) === this.constructAdress(this.tripInfo.pickup) &&
        // this.constructAdress(originalRsvpData.dropoff) === this.constructAdress(this.tripInfo.dropoff) &&
        originalRsvpData.passenger === this.tripInfo.passenger &&
        originalRsvpData.serviceCode === this.selectedService &&
        !hasExtraStopChanges && !this.charter.enabled) ||
        (
          this.charter.enabled && 
          (this.constructAdress(originalRsvpData.pickup) === this.constructAdress(this.tripInfo.pickup) ||
          (originalRsvpData.pickup.geofenceType === 'airport' && originalRsvpData.pickup.geofenceId === this.tripInfo.pickup.geofenceId)) &&
          originalRsvpData.pickupDate === this.charter.startDate && 
          moment(this.charter.startDate, 'MM/DD/YYYY hh:mmA').add(originalRsvpData.eta, 'seconds').format('MM/DD/YYYY hh:mmA') === this.charter.endDate
        ) ||
        this.waivePayments
      ) {
        fare = parseFloat(originalRsvpData.fare)
        travelInsurance = originalRsvpData.travelInsurance
        gasFee = originalRsvpData.gasFee
        portalFee = originalRsvpData.portalFee
      }
      let pickupType = ''
      let pickupFee = 0
      let flightName = ''
      let flightNumber = ''
      let dropoffFlightName = ''
      let dropoffFlightNumber = ''
      if (this.tripInfo.isAirportPickup) {
        pickupType = this.isMeetAndGreet ? 'MeetAndGreet' : 'Curbside'
        pickupFee = this.isMeetAndGreet ? selectedService.pickupFee : 0
        flightName = this.tripInfo.airline
        flightNumber = this.tripInfo.flightNo
      } else if (this.editData.tripInfo.aptGate !== '') {
        pickup.title += ' Apt/Gate: ' + this.editData.tripInfo.aptGate
        if (pickup.address2.indexOf(' Apt/Gate: ') > -1) {
          pickup.address2 = pickup.address2.split(' Apt/Gate: ')[0] + ' Apt/Gate: ' + this.editData.tripInfo.aptGate
        } else {
          pickup.address2 += ' Apt/Gate: ' + this.editData.tripInfo.aptGate
        }
      }
      if (!this.tripInfo.isAirportPickup && this.tripInfo.isAirportDropoff) {
        dropoffFlightName = this.tripInfo.airline
        dropoffFlightNumber = this.tripInfo.flightNo
      }
      if (this.editData.tripInfo.aptGateDropoff !== '') {
        dropoff.title += ' Apt/Gate: ' + this.editData.tripInfo.aptGateDropoff
        if (dropoff.address2.indexOf(' Apt/Gate: ') > -1) {
          dropoff.address2 = pickup.address2.split(' Apt/Gate: ')[0] + ' Apt/Gate: ' + this.editData.tripInfo.aptGateDropoff
        } else {
          dropoff.address2 += ' Apt/Gate: ' + this.editData.tripInfo.aptGateDropoff
        }
      }

      let airportFee = selectedService.airportFee

      let priceOverride = 0
      if (this.priceOverrideEnabled && !this.waivePayments) {
        priceOverride = this.priceOverride
        fare = parseFloat(this.priceOverride)
        pickupFee = 0
        // portalFee = 0
        if (selectedService.portalFee && selectedService.portalFeeType === '%') {
          portalFee = parseFloat((fare * (selectedService.portalFee / 100)).toFixed(2))
        }
      }
      let driverOverride = 0
      if (this.driverOverrideEnabled && this.paymentBonusEnabled) {
        driverOverride = this.driverOverride
      }

      let paymentBonus = 0
      if (this.paymentBonusEnabled){
        paymentBonus = this.paymentBonus
      }

      let tip = this.tripInfo.tip || 0
      let credits = this.tripInfo.credits
      let totalAmount = (airportFee + gasFee + portalFee + travelInsurance + parseFloat(tip) + fare + parseInt(pickupFee) - parseInt(credits))
      totalAmount = totalAmount < 0 ? 0 : totalAmount
      if (totalAmount % 1 > 0) {
        totalAmount = parseFloat(totalAmount.toFixed(2))
      }

      let brandId = this.tripInfo.brandId
      let index = Object.values(CONFIG.brands).findIndex(x => x.id === brandId)
      let isHudson = index > -1 && CONFIG.brands[index].code === 'pts'

      let cardId = this.customerInfo.creditCardId
      if (this.createUser.paymentMethod === 'directBilling') {
        cardId = null
      }
      let payload = {
        rsvpId: this.tripInfo.id,
        token: this.$store.state.session.sessionToken,
        brandId: this.tripInfo.brandId,
        guest: {
          firstName: this.guestInfo.firstName,
          lastName: this.guestInfo.lastName,
          mobile: this.guestInfo.mobile,
          countryCode: ''
        },
        legs: [
          {
            pickup: pickup,
            dropoff: this.tripInfo.dropoff,
            pickupTime: pickupDate.format('X'),
            passengers: this.tripInfo.passenger,
            luggage: this.tripInfo.luggage,
            amount: fare,
            originalPrice: servicePrice.original,
            flightNumber: flightNumber,
            flightName: flightName,
            dropoffFlightName: dropoffFlightName,
            dropoffFlightNumber: dropoffFlightNumber,
            asap: this.tripInfo.isAsap,
            etaInSec: this.tripInfo.eta,
            serviceTypeId: selectedService.id,
            serviceType: selectedService.code,
            pickupType: pickupType,
            pickupFee: pickupFee,
            distance: this.tripInfo.distance,
            notes: this.tripInfo.notes,
            airportFee: airportFee,
            gasFee: gasFee,
            travelInsurance: travelInsurance,
            extraStops: [],
            isCharter: this.charter.enabled,
            optinShared: null,
            portalFee: portalFee
          },
          null
        ],
        payment: {
          cardID: cardId,
          tip: tip,
          total: totalAmount,
          fare: fare,
          pickupFee: pickupFee,
          promo: credits,
          driverBonus: paymentBonus
        },
        wheelchair: this.wheelchairEnabled,
        serviceDog: this.serviceDogEnabled,
        source: 'A',
        deviceType: 'admin',
        overridePrice: priceOverride,
        overridePayment: driverOverride,
        hudson: isHudson,
        noEmail: !this.sendEmailReminder
      }
      // if (payload.guest.mobile) {
      //   payload.guest.mobile = `+${this.callingCode}${this.guestInfo.mobile}`
      //   payload.guest.countryCode = this.callingCode
      //   payload.guest.nationalNumber = this.guestInfo.mobile
      // }
      if (!CONFIG.useOldQuatation && selectedService.id > 5) {
        delete payload.legs[0].serviceType
      }
      let self = this
      if (self.extraStops && self.extraStops.length && !this.charter.enabled) {
        payload.legs[0].extraStops = []
        self.extraStops.forEach(stop => {
          payload.legs[0].extraStops.push({
            address1: stop.address1,
            address2: stop.address2,
            city: stop.city,
            state: stop.state,
            zip: stop.zip,
            lat: stop.lat,
            lng: stop.lng,
            title: stop.title
          })
        })
      }

      if (this.charter.enabled) {
        let startDate = moment(this.charter.startDate, 'MM/DD/YYYY hh:mmA')
        let endDate = moment(this.charter.endDate, 'MM/DD/YYYY hh:mmA')
        payload.isCharter = true
        payload.legs[0].pickupTime = startDate.format('X')
        payload.legs[0].etaInSec = endDate.diff(startDate, 'seconds')
        payload.legs[0].airportGeofenceId = this.airportPickup ? this.charter.airportGeoId : null
        delete payload.legs[0].dropoff
        // delete payload.legs[0].passengers
        // delete payload.legs[0].luggage
        delete payload.legs[0].distance
        delete payload.legs[0].dropoffFlightName
        delete payload.legs[0].dropoffFlightNumber
        delete payload.legs[0].travelInsurance
      }

      if (this.optin.enabled) {
        payload.legs[0].optinShared = {
          serviceTypeId: selectedService.id,
          amount: parseFloat(this.optinSharedPrice().toFixed(2))
        }
      }

      if (this.currency.code !== 'USD') {
        payload.legs[0].currency = {
          code: this.currency.code,
          exchangeRate: this.currency.value,
          amount: this.applyCurrency(fare, false),
          tipAmount: this.tipIntnl,
          pickupFee: this.applyCurrency(pickupFee, false),
          airportFee: this.applyCurrency(airportFee, false),
          gasFee: this.applyCurrency(gasFee, false),
          travelInsurance: this.applyCurrency(travelInsurance, false)
        }
        if (this.priceOverrideEnabled) {
          payload.legs[0].currency.amount = this.priceOverrideIntnl
        }
        if (this.currency.code === 'JPY') {
          payload.legs[0].currency.amount = parseFloat(xcurrency.format({code: 'JPY', val: payload.legs[0].currency.amount}, false).replaceAll(',', ''))
          payload.legs[0].currency.tipAmount = parseFloat(xcurrency.format({code: 'JPY', val: payload.legs[0].currency.tipAmount}, false).replaceAll(',', ''))
          payload.legs[0].currency.pickupFee = parseFloat(xcurrency.format({code: 'JPY', val: payload.legs[0].currency.pickupFee}, false).replaceAll(',', ''))
          payload.legs[0].currency.airportFee = parseFloat(xcurrency.format({code: 'JPY', val: payload.legs[0].currency.airportFee}, false).replaceAll(',', ''))
          payload.legs[0].currency.gasFee = parseFloat(xcurrency.format({code: 'JPY', val: payload.legs[0].currency.gasFee}, false).replaceAll(',', ''))
          payload.legs[0].currency.travelInsurance = parseFloat(xcurrency.format({code: 'JPY', val: payload.legs[0].currency.travelInsurance}, false).replaceAll(',', ''))
        }
      }

      if (this.waivePayments) payload.freeChange = true

      return payload
    },
    validateFields () {
      let noError = true
      this.cleanValidation()

      if (this.isCustomPickupLocation && !this.customPickupForceValid) {
        this.validation.pickupLocation = this.$t('rsvpDetails.edit.emptyField')
        noError = false
      }
      if (!this.isCustomPickupLocation && this.pickupAirportId === 0) {
        this.validation.pickupAirport = this.$t('rsvpDetails.edit.selectError')
        noError = false
      }
      if (this.isCustomDropoffLocation && !this.customDropoffForceValid && !this.charter.enabled) {
        this.validation.dropoffLocation = this.$t('rsvpDetails.edit.emptyField')
        noError = false
      }
      if (!this.isCustomDropoffLocation && this.dropoffAirportId === 0 && !this.charter.enabled) {
        this.validation.dropoffLocation = this.$t('rsvpDetails.edit.selectError')
        noError = false
      }
      let dateObj = this.charter.enabled ? this.startDate : this.pickupDate
      let selectedDate = dateObj.date + ' ' + dateObj.hour + ':' + dateObj.minute + dateObj.amPm
      let selectedDateFormat = 'MM/DD/YYYY hh:mmA'
      if (this.$settings.get('MilitaryTimeBackoffice') === '1') {
        selectedDate = dateObj.date + ' ' + dateObj.hour + ':' + dateObj.minute
        selectedDateFormat = 'MM/DD/YYYY HH:mm'
      }
      let adminTimeBuffer = this.$store.state.settings.adminTimeBuffer
      let timezone = this.$store.state.rsvpDetails.edit.timezone
      if (
        moment.tz(selectedDate, selectedDateFormat, timezone).format('x') < moment().add(adminTimeBuffer, 'minutes').format('x') && !this.pickupAsap
      ) {
        if (this.charter.enabled) {
          this.charterValidation.startDate = this.$t('rsvpDetails.edit.adminTimeBufferError', {min: adminTimeBuffer})
        } else {
          this.validation.pickupTime = this.$t('rsvpDetails.edit.adminTimeBufferError', {min: adminTimeBuffer})
        }
        noError = false
      }
      if (!this.isFreshDeliveries && !this.$store.state.rsvpDetails.lyft.enabled) {
        if ((this.airportPickup || (CONFIG.featureToggle.requiredDropoffAirport && this.airportDropoff)) &&
          (!this.editData.tripInfo.airline || this.editData.tripInfo.airline.trim() === '') && !this.pickupAsap
        ) {
          this.validation.airline = this.$t('rsvpDetails.edit.emptyField')
          noError = false
        }
        if ((this.airportPickup || (CONFIG.featureToggle.requiredDropoffAirport && this.airportDropoff)) &&
          (!this.editData.tripInfo.flightNo || this.editData.tripInfo.flightNo.trim() === '') && !this.pickupAsap
        ) {
          this.validation.flight = this.$t('rsvpDetails.edit.emptyField')
          noError = false
        }
      }
      if ((this.bookingForSomeone || this.isCorporate) && this.editData.guestInfo.firstName.trim() === '') {
        this.validation.guestFirstName = this.$t('rsvpDetails.edit.emptyField')
        noError = false
      }
      if ((this.bookingForSomeone || this.isCorporate) && this.editData.guestInfo.lastName.trim() === '') {
        this.validation.guestLastName = this.$t('rsvpDetails.edit.emptyField')
        noError = false
      }
      if (this.isCorporate && this.editData.guestInfo.mobile.trim() === '') {
        this.validation.guestMobile = this.$t('rsvpDetails.edit.emptyField')
        noError = false
      }
      if (!this.selectedService) {
        this.validation.service = this.$t('rsvpDetails.edit.selectService')
        if (this.disableAllService) {
          this.validation.service = this.$t('rsvpDetails.edit.serviceRestrictions')
        }
        noError = false
      }
      if (this.priceOverrideEnabled && this.priceOverride.toString().trim() === '') {
        this.validation.priceOverride = this.$t('rsvpDetails.edit.emptyField')
        noError = false
      }
      if (this.priceOverrideEnabled && parseFloat(this.priceOverride) <= 0) {
        this.validation.priceOverride = this.$t('rsvpDetails.edit.zeroValue')
        noError = false
      }
      if (this.driverOverrideEnabled && this.driverOverride.toString().trim() === '') {
        this.validation.driverOverride = this.$t('rsvpDetails.edit.emptyField')
        noError = false
      }
      if (this.driverOverrideEnabled && parseFloat(this.driverOverride) <= 0) {
        this.validation.driverOverride = this.$t('rsvpDetails.edit.zeroValue')
        noError = false
      }
      let fare = parseFloat(this.paymentBonusComputation.limitAmount).toFixed(2)
      if (this.paymentBonusEnabled && parseFloat(this.paymentBonus) > this.paymentBonusComputation.limitAmount) {
        this.validation.paymentBonus = this.$t('rsvpDetails.edit.beyondLimit', {fare: fare})
        noError = false
      }

      if (this.paymentBonusEnabled && !!parseFloat(this.paymentBonus) === false) {
        this.validation.paymentBonus = 'Not a number.'
        noError = false
      }

      if (this.paymentBonusEnabled && parseFloat(this.paymentBonus) <= 0) {
        this.validation.paymentBonus = 'Value cannot be less than or equal to zero'
        noError = false
      }

      if (this.isNewRsvp) {
        if (!this.customerInfo.firstName && !this.createUser.show) {
          this.validation.userSelected = 'No target customer.'
          noError = false
        }
        if (!this.createUser.notes && this.createUser.paymentMethod === 'otherMeans') {
          this.validation.userNotes = this.$t('rsvpDetails.edit.emptyField')
          noError = false
        }
        if (!this.createUser.noteStatus && this.createUser.paymentMethod === 'otherMeans') {
          this.validation.userNotesStatus = 'Please select note status'
          noError = false
        }
        if (this.createUser.paymentMethod === 'newCard') { 
          if (this.isD2D) {
            if (
              !this.stripeStore.cardOwner || !this.stripeStore.address ||
              !this.stripeStore.userCity || !this.stripeStore.userZip ||
              this.stripeStore.userState == '..' ||
              (this.stripeStore.userState == '..' && !this.stripeStore.userCountry)
            ) {
              noError = false
            }
          } else {
            if (!this.createUser.cardOwner) {
              this.validation.userCardOwner = this.$t('rsvpDetails.edit.emptyField')
              noError = false
            }
            if (!this.createUser.creditCard) {
              this.validation.userCreditCard = this.$t('rsvpDetails.edit.emptyField')
              noError = false
            }
            if (!this.createUser.cardExp) {
              this.validation.userCardExp = this.$t('rsvpDetails.edit.emptyField')
              noError = false
            }
            if (!this.createUser.cvv) {
              this.validation.userCvv = this.$t('rsvpDetails.edit.emptyField')
              noError = false
            }
            if (!this.createUser.address) {
              this.validation.userAddress = this.$t('rsvpDetails.edit.emptyField')
              noError = false
            }
            if (!this.createUser.city ) {
              this.validation.userCity = this.$t('rsvpDetails.edit.emptyField')
              noError = false
            }
            if (this.createUser.country === '' && !this.createUser.state) {
              this.validation.userCountry = this.$t('rsvpDetails.edit.emptyField')
              noError = false
            }
            if (this.createUser.state === '..') {
              this.validation.userState = this.$t('rsvpDetails.edit.emptyField')
              noError = false
            }
          }
        }
        // if (!this.createUser.zip && this.createUser.paymentMethod === 'newCard') {
        //  this.validation.userZip = this.$t('rsvpDetails.edit.emptyField')
        //  noError = false
        // }
        if (this.customerInfo.firstName && this.createUser.selectedCard === '' && this.createUser.paymentMethod === 'oldCard') {
          this.validation.creditCard = this.$t('rsvpDetails.edit.emptyField')
          noError = false
        }
        if (!this.isD2D) {
          if (this.createUser.show && !this.createUser.firstName) {
            this.validation.userFirstName = this.$t('rsvpDetails.edit.emptyField')
            noError = false
          }
          if (this.createUser.show && !this.createUser.lastName) {
            this.validation.userLastName = this.$t('rsvpDetails.edit.emptyField')
            noError = false
          }
          if (this.createUser.show && !this.createUser.email) {
            this.validation.userEmail = this.$t('rsvpDetails.edit.emptyField')
            noError = false
          }
          if (this.createUser.show && !this.createUser.mobile) {
            this.validation.userMobile = this.$t('rsvpDetails.edit.emptyField')
            noError = false
          }
          if (this.createUser.show && this.createUser.paymentMethod === 'newCard' && !this.createUser.cardOwner) {
            this.validation.userCardOwner = this.$t('rsvpDetails.edit.emptyField')
            noError = false
          }
          if (this.createUser.show && this.createUser.paymentMethod === 'newCard' && !this.createUser.creditCard) {
            this.validation.userCreditCard = this.$t('rsvpDetails.edit.emptyField')
            noError = false
          }
          if (this.createUser.show && this.createUser.paymentMethod === 'newCard' && !this.createUser.cardExp) {
            this.validation.userCardExp = this.$t('rsvpDetails.edit.emptyField')
            noError = false
          }
          if (this.createUser.show && this.createUser.paymentMethod === 'newCard' && !this.createUser.cvv) {
            this.validation.userCvv = this.$t('rsvpDetails.edit.emptyField')
            noError = false
          }
          if (this.createUser.show && this.createUser.paymentMethod === 'newCard' && !this.createUser.address) {
            this.validation.userAddress = this.$t('rsvpDetails.edit.emptyField')
            noError = false
          }
          if (this.createUser.show && this.createUser.paymentMethod === 'newCard' && !this.createUser.city) {
            this.validation.userCity = this.$t('rsvpDetails.edit.emptyField')
            noError = false
          }
        }
        // if (this.createUser.show && this.createUser.paymentMethod === 'newCard' && !this.createUser.state) {
        //   this.validation.userState = this.$t('rsvpDetails.edit.emptyField')
        //   noError = false
        // }
        // if (this.createUser.show && this.createUser.paymentMethod === 'newCard' && !this.createUser.zip) {
        //   this.validation.userZip = this.$t('rsvpDetails.edit.emptyField')
        //   noError = false
        // }
        if (this.createUser.show && !this.createUser.paymentMethod === 'newCard' && !this.createUser.notes) {
          this.validation.userNotes = this.$t('rsvpDetails.edit.emptyField')
          noError = false
        }
        if (this.createUser.show && !this.createUser.paymentMethod === 'newCard' && !this.createUser.noteStatus) {
          this.validation.userNotesStatus = this.$t('rsvpDetails.edit.emptyField')
          noError = false
        }

        if (this.isD2D && this.createUser.show) {
          let self = this
          let fields = ['birthday', 'address', 'city', 'zip', 'country']
          fields.forEach(x => {
            if (!self.d2dUser[x]) {
              self.d2dValidation[x] = self.$t('rsvpDetails.edit.emptyField')
              noError = false
            }
          })
        }
      }

      let self = this
      if (this.extraStops && !this.charter.enabled) {
        if (this.extraStops.length) {
          this.extraStops.forEach(stop => {
            if (!stop.lat || !stop.lng) {
              self.$store.commit('rsvpDetails/setExtraStopData', {
                id: stop.id,
                fields: { error: this.$t('rsvpDetails.edit.emptyField') }
              })
              noError = false
            }
          })
        }
      }

      if (this.charter.enabled) {
        let startDate = moment(self.charter.startDate, 'MM/DD/YYYY hh:mmA')
        let endDate = moment(self.charter.endDate, 'MM/DD/YYYY hh:mmA')
        if (startDate.diff(endDate, 'minutes') > 0) {
          noError = false
        }
        if (endDate.diff(startDate, 'minutes') > this.charterMaxDura) {
          noError = false
        }
        if (self.charter.startDate === self.charter.endDate) {
          noError = false
        }
      }

      return noError
    },
    cleanValidation () {
      this.validation = {
        pickupLocation: '',
        pickupAirport: '',
        dropoffLocation: '',
        dropoffAirport: '',
        pickupTime: '',
        airline: '',
        flight: '',
        guestFirstName: '',
        guestLastName: '',
        service: '',
        tip: '',
        priceOverride: '',
        driverOverride: '',
        paymentBonus: '',
        userSelected: '',
        creditCard: '',
        userFirstName: '',
        userLastName: '',
        userEmail: '',
        userMobile: '',
        userCardOwner: '',
        userCreditCard: '',
        userCardExp: '',
        userCvv: '',
        userAddress: '',
        userCity: '',
        userState: '',
        userZip: '',
        userNotes: '',
        userNotesStatus: ''
      },
      this.d2dValidation = {
        birthday: '',
        address: '',
        city: '',
        zip: '',
        country: ''
      }
      this.driverOverrideValidation = {
        pickupAirport: true,
        dropoffAirport: true,
        selectedService: true,
        pickup: true,
        dropoff: true,
        servicePickup: true,
        serviceDropoff: true,
        selectAirportPickup: true,
        selectAirportDropoff: true
      }
    },
    showFlightInput () { return this.tripInfo.isAirportDropoff || this.tripInfo.isAirportPickup },
    initNewRsvp () {
      let self = this
      self.cleanValidation()

      let timeNow = moment()
      let timeFixed = parseInt(timeNow.format('mm')) % 5
      if (timeFixed > 0) {
        timeNow = timeNow.add(5, 'minutes').subtract(timeFixed, 'minutes')
      }
      timeNow = timeNow.add(this.$settings.get('AdminTimeBuffer'), 'minutes')
      let timeFormat = this.$settings.get('MilitaryTimeBackoffice') === '1' ? 'MM/DD/YYYY HH:mm' : 'MM/DD/YYYY hh:mmA'
      this.$store.commit('rsvpDetails/setPickupDate', timeNow.format(timeFormat))
      this.$store.commit('rsvpDetails/setMtechData', {flightDate: timeNow.format(timeFormat)})
      this.$store.commit('rsvpDetails/setCharterData', {
        startDate: timeNow.format(timeFormat),
        // endDate: timeNow.add(self.charterMaxDura, 'minutes').format(timeFormat)
      })
    },
    initCreateUser () {
      this.createUser.show = true

      this.$store.commit('rsvpDetails/setCustomerInfo', {
        id: '',
        firstName: '',
        lastName: '',
        email: '',
        mobile: '',
        countryCode: ''
      })
      this.$store.commit('rsvpDetails/setIsCorporate', false)
    },
    isNumber (evt) {
      let charCode = (evt.which) ? evt.which : evt.keyCode;
      if ((charCode > 31 && (charCode < 48 || charCode > 57))) {
        evt.preventDefault()
        return
      }
      return true;
    },
    formatExpCC () {
      let cardExp = this.createUser.cardExp
      if (cardExp.length === 2 && !isNaN(cardExp)) {
        this.createUser.cardExp = cardExp + '/'
      }
    },
    charCountChecker (event) {
      if (this.createUser.notes.length > this.createUser.notesMaxChar) {
        this.createUser.notes = this.createUser.notes.substring(0, this.createUser.notesMaxChar)
        event.preventDefault()
        return
      }
    },
    createRsvp () {
      let self = this
      if (!self.validateFields() || self.$store.state.rsvpDetails.addLoading) { return }
      
      if (self.isD2D && this.createUser.paymentMethod === 'newCard') {
        if (this.createUser.show) {
          this.createD2Duser()
          return
        }
        self.$store.commit('global/setLoaderData', {show: true})
        this.d2dStripeSetup(this.customerInfo.id)
        return
      }
      self.$store.commit('global/setLoaderData', {show: false})

      if (this.pickupAsap && !this.charter.enabled) {
        // self.$store.commit('rsvpDetails/setAsapShow', true)
        // self.asapSubmit()
        self.$store.commit('rsvpDetails/setAsapData', self.buildCreateRsvpData())
        self.$store.commit('rsvpDetails/setAsapModal', true)
      } else {
        self.$store.commit('rsvpDetails/setAddLoading', true)
        ApiRsvpAdd.createRsvp(self, self.buildCreateRsvpData(), function (res) {
          self.$store.commit('rsvpDetails/setAddLoading', false)
          if (res.error) {
            self.$store.commit('rsvpDetails/setSubmitError', {
              show: true,
              message: res.error.message
            })
            return
          }
          self.saveNotes(res.reservationId[0])

          let user = self.$store.state.session.user
          let userFullName = user.firstName + ' ' + user.lastName
          let userEmail = user.email
          self.$sdk.post('/admin/rsvp/' + res.reservationId[0] + '/notes', {
            token: self.$store.state.session.sessionToken,
            message: 'This RSVP was created by admin  ' + userFullName + ' (' + userEmail + ')',
            status: 'resolved',
            action: 'rsvp_add'
          }, r => {
            setTimeout(function () {
              self.$store.commit('rsvpDetails/setIsAdd', false)
              window.location = '/#/reservation/' + res.reservationId[0]
            }, 2000)
          })
        })
      }
    },
    // asapSubmit () {
    //   let self = this
    //   let data = self.buildCreateRsvpData()

    //   self.$store.commit('rsvpDetails/setAsapLoading', true)
    //   self.$store.commit('rsvpDetails/setFoundDriver', false)
    //   self.$store.commit('rsvpDetails/setNoDriver', false)

    //   ApiRsvpAdd.getSequestId(self, self.buildCreateRsvpData(), function (r) {
    //     if (r.error) {
    //       self.$store.commit('rsvpDetails/setNoDriver', true)
    //       self.$store.commit('rsvpDetails/setAsapLoading', false)
    //       return
    //     }

    //     self.$store.commit('rsvpDetails/setAsapBidId', r.bidId)
    //     self.getDriver()
    //   })
    // },
    // getDriver () {
    //   let self = this
    //   let sequenceID = self.$store.state.rsvpDetails.asap.bidId

    //   if (!sequenceID) {
    //     self.asapSubmit()
    //     return
    //   }

    //   self.$store.commit('rsvpDetails/setNoDriver', false)
    //   self.$sdk.get('/reservations/sequence/' + sequenceID, {}, r => {
    //     if (r.error) {
    //       self.$store.commit('rsvpDetails/setNoDriver', true)
    //       return
    //     }

    //     self.$store.commit('rsvpDetails/setAsapBidId', r.bidId)

    //     if (r.status === 'searching' || r.status === 'fail') {
    //       setTimeout(function () {
    //         self.getDriver()
    //       }, 1000)
    //       return
    //     }

    //     if (r.status === 'rejected') {
    //       self.$store.commit('rsvpDetails/setNoDriver', true)
    //       return
    //     }

    //     self.$store.commit('rsvpDetails/setFoundDriver', true)
    //     self.$store.commit('rsvpDetails/setDriverInfoLoading', true)
    //     self.saveNotes(r.rsvpId)
    //     self.$sdk.get('/admin/rsvp/' + r.rsvpId, {
    //       token: self.$store.state.session.sessionToken
    //     }, res => {
    //       self.$store.commit('rsvpDetails/setDriverInfoLoading', false)
    //       self.$store.commit('rsvpDetails/setDriverInfo', {
    //         fullName: r.provider.firstName + ' ' + r.provider.lastName,
    //         email: r.provider.email,
    //         rating: r.provider.rating,
    //         profilePic: r.provider.avatarUrl,
    //         vehiclePick: r.vehicle.photo,
    //         model: r.vehicle.model,
    //         plate: r.vehicle.plate
    //       })

    //     })

    //     let userFullName = self.createUser.firstName + ' ' + self.createUser.lastName
    //     let userEmail = self.createUser.email
    //     if (self.customerInfo.firstName) {
    //       userFullName = self.customerInfo.firstName + ' ' + self.customerInfo.lastName
    //       userEmail = self.customerInfo.email
    //     }
    //     self.$sdk.post('/admin/rsvp/' + r.rsvpId + '/notes', {
    //       token: self.$store.state.session.sessionToken,
    //       message: 'This RSVP was created by admin  ' + userFullName + ' (' + userEmail + ')',
    //       status: 'resolved',
    //       action: 'rsvp_add'
    //     }, res => {

    //     })
    //   })
    // },
    buildCreateRsvpData () {
      let pickup = this.tripInfo.pickup
      let timezone = this.$store.state.rsvpDetails.edit.timezone
      let pickupDate = moment.tz(this.tripInfo.pickupDate, 'MM/DD/YYYY ' + featureToggle.timeFormat(this, false), timezone)
      let selectedService = this.servicePrices[this.selectedService]
      let servicePrice = selectedService && selectedService.prices ? selectedService.prices[this.passenger - 1] : 0
      let fare = parseFloat(servicePrice.price || 0)
      let pickupType = ''
      let pickupFee = 0
      let flightName = ''
      let flightNumber = ''
      let dropoffFlightName = ''
      let dropoffFlightNumber = ''
      if (this.tripInfo.isAirportPickup) {
        pickupType = this.isMeetAndGreet ? 'MeetAndGreet' : 'Curbside'
        pickupFee = this.isMeetAndGreet ? selectedService.pickupFee : 0
        flightName = this.tripInfo.airline
        flightNumber = this.tripInfo.flightNo
      } else if (this.editData.tripInfo.aptGate !== '') {
        pickup.title += ' Apt/Gate: ' + this.editData.tripInfo.aptGate
        if (pickup.address2.indexOf(' Apt/Gate: ') > -1) {
          pickup.address2 = pickup.address2.split(' Apt/Gate: ')[0] + ' Apt/Gate: ' + this.editData.tripInfo.aptGate
        } else {
          pickup.address2 += ' Apt/Gate: ' + this.editData.tripInfo.aptGate
        }
      }
      if (!this.tripInfo.isAirportPickup && this.tripInfo.isAirportDropoff) {
        dropoffFlightName = this.tripInfo.airline
        dropoffFlightNumber = this.tripInfo.flightNo
      }

      let airportFee = selectedService ? selectedService.airportFee : 0
      let gasFee = selectedService ? selectedService.gasFee : 0
      if (!gasFee) gasFee = 0
      let travelInsurance = selectedService ? selectedService.travelInsurance : 0
      let portalFee = this.paymentSummaryData.portalFee
      let priceOverride = 0
      if (this.priceOverrideEnabled) {
        priceOverride = this.priceOverride
        fare = parseFloat(this.priceOverride)
        pickupFee = 0
        // portalFee = 0
      }
      let driverOverride = 0
      if (this.driverOverrideEnabled) {
        driverOverride = this.driverOverride
      }

      let paymentBonus = 0
      if (this.paymentBonus) {
        paymentBonus = this.paymentBonus
      }

      let tip = this.tripInfo.tip || 0
      let credits = this.tripInfo.credits || 0
      let totalAmount = (airportFee + travelInsurance + gasFee + portalFee + parseFloat(tip) + fare + parseInt(pickupFee) - parseInt(credits))
      totalAmount = totalAmount < 0 ? 0 : totalAmount

      let payload = {
        token: this.$store.state.session.sessionToken,
        brandId: this.tripInfo.brandId,
        guest: {
          firstName: this.guestInfo.firstName,
          lastName: this.guestInfo.lastName,
          mobile: this.guestInfo.mobile,
          countryCode: ''
        },
        legs: [
          {
            pickup: pickup,
            dropoff: this.tripInfo.dropoff,
            pickupTime: pickupDate.format('X'),
            passengers: this.passenger,
            luggage: this.luggage,
            amount: fare,
            originalPrice: servicePrice.original,
            flightNumber: flightNumber,
            flightName: flightName,
            dropoffFlightName: dropoffFlightName,
            dropoffFlightNumber: dropoffFlightNumber,
            asap: false,
            etaInSec: this.tripInfo.eta,
            serviceTypeId: selectedService ? selectedService.id : 0,
            pickupType: pickupType,
            pickupFee: pickupFee,
            distance: this.tripInfo.distance,
            notes: this.tripInfo.notes,
            airportFee: airportFee,
            travelInsurance: travelInsurance,
            gasFee: gasFee,
            optinShared: null,
            portalFee: portalFee
          },
          null
        ],
        payment: {
          // cardID: this.customerInfo.creditCardId,
          tip: tip,
          total: totalAmount,
          fare: fare,
          pickupFee: pickupFee,
          promo: credits
        },
        wheelchair: this.wheelchairEnabled,
        serviceDog: this.serviceDogEnabled,
        source: 'A',
        deviceType: 'admin',
        overridePrice: priceOverride,
        overridePayment: driverOverride,
        hudson: true
      }

      if (this.$store.state.rsvpDetails.lyft.enabled) {
        delete payload.legs[0].pickup.geofenceId
        delete payload.legs[0].pickup.geofenceType
        delete payload.legs[0].pickup.geofenceName
        delete payload.legs[0].dropoff.geofenceId
        delete payload.legs[0].dropoff.geofenceType
        delete payload.legs[0].dropoff.geofenceName
        delete payload.legs[0].serviceTypeId
        let lyftServices = this._edit.services[this._edit.selectedService]
        payload.legs[0].lyft = {
          ride_type: lyftServices.ride_type,
          cost_token: lyftServices.cost_token,
          driver_availability_token: lyftServices.driver_availability_token || ''
        }
        payload.lyft = true
        payload.source = 'C'
      }

      if (CONFIG.brands.find(x => x.id === this.editData.tripInfo.brandId).code === 'd2d') {
        payload.d2dPayment = 1
      }
      // if (payload.guest.mobile) {
      //   payload.guest.mobile = `+${this.callingCode}${this.guestInfo.mobile}`
      //   payload.guest.countryCode = this.callingCode
      //   payload.guest.nationalNumber = this.guestInfo.mobile
      // }
      if (this.useOldQuatation) {
        payload.legs[0].serviceType = selectedService.code
      }

      if (this.customerInfo.id) {
        payload.consumerId = this.customerInfo.id
      } else if (this.createUser.firstName) {
        // let mobileNumber = phoneUtil.parse(this.createUser.mobile, "")
        let countryCode = this.callingCode
        let nationalNumber = this.createUser.mobile
        payload.consumer = {
          firstName: this.createUser.firstName,
          lastName: this.createUser.lastName,
          // phone: this.createUser.mobile,
          phone: `+${countryCode}${nationalNumber}`,
          email: this.createUser.email,
          wheelchair: this.wheelchairEnabled,
          serviceDog: this.serviceDogEnabled,
          countryCode: countryCode,
          nationalNumber: nationalNumber
        }
      }

      if (this.isCorporate) {
        payload.consumerId = this.customerInfo.corpConsId
      }
      if (this.customerInfo.firstName && this.createUser.selectedCard === 'new' ||
        (this.createUser.paymentMethod === 'newCard')
      ) {
        payload.payment.card = {
          title: 'mastercard',
          number: this.createUser.creditCard,
          expiration: this.createUser.cardExp,
          name: this.createUser.cardOwner,
          cvv: this.createUser.cvv,
          billingAddress: this.createUser.address,
          billingCity: this.createUser.city,
          billingState: this.createUser.state,
          billingCountry: this.createUser.country,
          billingZip: this.createUser.zip,
        }
      } else if (this.createUser.paymentMethod === 'otherMeans' || this.createUser.paymentMethod === 'directBilling') {
        payload.payment.cardID = null
      } else if (this.createUser.paymentMethod === 'oldCard') {
        payload.payment.cardID = this.userCardlist[this.createUser.selectedCard].id
      }

      if (this.pickupAsap) {
        payload.legs[0].asap = true
        payload.legs[0].pickupTime = moment().unix()
      }

      if (this.extraStops) {
        if (this.extraStops.length) {
          payload.legs[0].extraStops = []
          this.extraStops.forEach(stop => {
            payload.legs[0].extraStops.push({
              address1: stop.address1,
              address2: stop.address2,
              city: stop.city,
              state: stop.state,
              zip: stop.zip,
              lat: stop.lat,
              lng: stop.lng,
              title: stop.title
            })
          })
        }
      }

      if (this.charter.enabled) {
        let startDate = moment(this.charter.startDate, 'MM/DD/YYYY hh:mmA')
        let endDate = moment(this.charter.endDate, 'MM/DD/YYYY hh:mmA')
        payload.isCharter = true
        payload.legs[0].pickupTime = startDate.format('X')
        payload.legs[0].etaInSec = endDate.diff(startDate, 'seconds')
        payload.legs[0].airportGeofenceId = this.airportPickup ? this.charter.airportGeoId : null
        delete payload.legs[0].dropoff
        // delete payload.legs[0].passengers
        // delete payload.legs[0].luggage
        delete payload.legs[0].distance
        delete payload.legs[0].dropoffFlightName
        delete payload.legs[0].dropoffFlightNumber
        delete payload.legs[0].travelInsurance
      }

      if (this.optin.enabled) {
        payload.legs[0].optinShared = {
          serviceTypeId: selectedService.id,
          amount: parseFloat(this.optinSharedPrice().toFixed(2))
        }
      }

      if (this.mtech.enabled) {
        payload.legs[0].serviceTypeId = null
        payload.legs[0].serviceType = this.selectedService
        // payload.legs[0].mtechServiceType = this.selectedService
        payload.legs[0].mtechServiceType = this.mtech.services[this.selectedService].product
        payload.legs[0].pickupType = 'Curbside'
        payload.legs[0].pickupFee = 0
        payload.legs[0].flightTime = moment.tz(this.mtech.flightDate, 'MM/DD/YYYY ' + featureToggle.timeFormat(this, false), timezone).format('X')
        payload.legs[0].flightAirline = this.tripInfo.airlineCode
        payload.legs[0].flightDomestic = this.mtech.isDomestic
        // if (this.mtech.services[this.selectedService].pickupTimes.length) {
        //   payload.legs[0].pickupTime = this.mtech.services[this.selectedService].pickupTimes[0].unix
        // }

        payload.legs[0].mtechPrice = this.mtech.services[this.selectedService].price.amount.toFixed(2)
        let breakdown = this.mtech.services[this.selectedService].price.breakdown
        let priceBeforeCredits = this.mtech.services[this.selectedService].price.amount + this.mtech.services[this.selectedService].price.addMarkup
        payload.legs[0].amount = parseFloat(priceBeforeCredits.toFixed(2))
        payload.legs[0].originalPrice = priceBeforeCredits.toFixed(2)
        let total = this.mtech.services[this.selectedService].price.amount + this.mtech.services[this.selectedService].price.addMarkup
        payload.payment.total = total + parseFloat(tip) - parseInt(credits)
        payload.payment.fare = total + parseFloat(tip) - parseInt(credits)
        payload.quoteId = this.mtech.quoteId
        payload.isMTech = this.mtech.enabled
      }

      if (this.currency.code !== 'USD') {
        payload.legs[0].currency = {
          code: this.currency.code,
          exchangeRate: this.currency.value,
          amount: this.applyCurrency(fare, false),
          tipAmount: this.tipIntnl,
          pickupFee: this.applyCurrency(pickupFee, false),
          airportFee: this.applyCurrency(airportFee, false),
          gasFee: this.applyCurrency(gasFee, false),
          travelInsurance: this.applyCurrency(travelInsurance, false)
        }
        if (this.priceOverrideEnabled) {
          payload.legs[0].currency.amount = this.priceOverrideIntnl
        }
        if (this.currency.code === 'JPY') {
          payload.legs[0].currency.amount = parseFloat(xcurrency.format({code: 'JPY', val: payload.legs[0].currency.amount}, false).replaceAll(',', ''))
          payload.legs[0].currency.tipAmount = parseFloat(xcurrency.format({code: 'JPY', val: payload.legs[0].currency.tipAmount}, false).replaceAll(',', ''))
          payload.legs[0].currency.pickupFee = parseFloat(xcurrency.format({code: 'JPY', val: payload.legs[0].currency.pickupFee}, false).replaceAll(',', ''))
          payload.legs[0].currency.airportFee = parseFloat(xcurrency.format({code: 'JPY', val: payload.legs[0].currency.airportFee}, false).replaceAll(',', ''))
          payload.legs[0].currency.gasFee = parseFloat(xcurrency.format({code: 'JPY', val: payload.legs[0].currency.gasFee}, false).replaceAll(',', ''))
          payload.legs[0].currency.travelInsurance = parseFloat(xcurrency.format({code: 'JPY', val: payload.legs[0].currency.travelInsurance}, false).replaceAll(',', ''))
        }
      }

      return payload
    },
    removeSelectedService () { this.selectService('','') },
    saveNotes (rsvpId) {
      let self = this
      if (self.createUser.paymentMethod === 'otherMeans') {
        self.$sdk.post('/admin/rsvp/' + rsvpId + '/notes', {
          token: self.$store.state.session.sessionToken,
          message: self.createUser.notes,
          status: self.createUser.noteStatus,
          action: 'rsvp_add'
        }, res => {
          console.log('res NOTES', res)
        })
      }
    },
    //code
    clearDriverPayment () {
      if (
        this.driverOverrideValidation.pickupAirport && this.driverOverrideValidation.dropoffAirport &&
        this.driverOverrideValidation.servicePickup && this.driverOverrideValidation.serviceDropoff &&
        this.driverOverrideValidation.selectedService && this.driverOverrideValidation.pickup &&
        this.driverOverrideValidation.dropoff && this.driverOverrideValidation.selectAirportPickup &&
        this.driverOverrideValidation.selectAirportDropoff && this.$store.state.rsvpDetails.basicInfo.tripInfo.overridePayment > 0
      ) {
        this.driverOverrideEnabled = true
        this.driverOverride = this.$store.state.rsvpDetails.basicInfo.tripInfo.overridePayment
        // this.paymentBonusEnabled = true
        // this.paymentBonus = this.$store.state.rsvpDetails.basicInfo.tripInfo.paymentBonus
      } else {
        this.driverOverrideEnabled = false
        this.driverOverride = 0
        // this.paymentBonusEnabled = false
        // this.paymentBonus = 0
      }
    },
    setScreenSize() {
      if( /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ) {
        this.isDesktop = false
        this.isMobile = true
      } else {
        this.isDesktop = true
        this.isMobile = false
      }
    },
    resetPaymentMethod () {
      this.createUser.paymentMethod = ''
      if (this.isCorporate) {
        this.createUser.paymentMethod = 'directBilling'
      }
    },
    setDefaultTip () {
      if (!this.isNewRsvp || this.$store.state.rsvpDetails.isManualTip) { return }

      let serviceData = this.servicePrices[this.selectedService]
      if (!this.selectedService || !serviceData || this.serviceDisable(this.selectedService, serviceData)) {
        this.$store.commit('rsvpDetails/setTip', 0)
        return
      }

      let tip = serviceData.defaultTip
      let prices = serviceData.prices
      tip = tip / 100
      let pax = this.tripInfo.passenger || 1
      this.$store.commit('rsvpDetails/setTip', this.toFixed(prices[pax - 1].price * tip))
    },
    forceManualTip () {
      this.$store.commit('rsvpDetails/setIsManualTip', true)
    },
    addExtraStop () {
      this.$store.commit('rsvpDetails/addExtraStop', {
        id: moment().format('x'),
        address1: '',
        address2: '',
        city: '',
        state: '',
        zip: '',
        country: '',
        title: '',
        lat: '',
        lng: '',
        error: ''
      })
    },
    removeExtraStop () {
      let getQuote = false
      if (this.tripInfo.extraStops.length && this.tripInfo.extraStops[0].lat && this.tripInfo.extraStops[0].lng) {
        getQuote = true
      }
      this.$store.commit('rsvpDetails/removeExtraStop')
      if (getQuote) {
        this.getQuote()
      }
    },
    extraData (data) {
      return {
        id: data.id,
        field: 'extraStop',
        disabled: this.waivePayments,
        brandId: this.editData.tripInfo.brandId,
        error: data.error
      }
    },
    locationNewStop () {
      let self = this
      if (self.quoteDelay) {
        clearTimeout(self.quoteDelay)
      }
      self.quoteDelay = setTimeout(function () {
        if (!CONFIG.useOldQuatation) {
          self.selectedService = ''
        }
        self.getQuote()
      }, 500)
      this.clearDriverPayment()
    },
    changeCharterDate (field) {
      let self = this
      self[field].date = moment(self[field].date).format('MM/DD/YYYY')
      self.updateCharterTime(field)
    },
    updateCharterTime (field) {
      let self = this
      let _date = this[field].date + ' ' + this[field].hour + ':' +
        this[field].minute + '' + this[field].amPm
      if (this.$settings.get('MilitaryTimeBackoffice') === '1') {
        _date = this[field].date + ' ' + this[field].hour + ':' + this[field].minute
      }
      let obj = {}
      obj[field] = _date
      this.$store.commit('rsvpDetails/setCharterData', obj)
      // this.sharedAllowedTime(this.selectedService)

      if (self.charter.startDate && self.charter.endDate) {
        let startDate = moment(self.charter.startDate, 'MM/DD/YYYY hh:mmA')
        let endDate = moment(self.charter.endDate, 'MM/DD/YYYY hh:mmA')
        if (startDate.diff(endDate, 'minutes') > 0) {
          self.charterValidation.endDate = 'End date must be later than start date.'
          return
        }
        if (endDate.diff(startDate, 'minutes') > self.charterMaxDura) {
          let time = moment.duration(self.charterMaxDura, 'minutes').humanize({h: Infinity})
          self.charterValidation.endDate = `Max duration for charter booking is ${time}.`
          return
        }
        if (self.charter.startDate === self.charter.endDate) {
          self.charterValidation.startDate = `Invalid date range.`
          self.charterValidation.endDate = `Invalid date range.`
          return
        }

        let selectedService = self.selectedService.replace('a', '')
        let minDurationServiceId = self.settings.charterMnDurationServiceId
        if (selectedService && minDurationServiceId.indexOf(selectedService) > -1) {
          if (endDate.diff(startDate, 'minutes') < self.settings.charterMinDuration) {
            let time = moment.duration(self.settings.charterMinDuration, 'minutes').humanize({h: Infinity})
            self.charterValidation.endDate = `Minimum duration for this particular service is ${time}.`
            return
          }
        }
      }
      setTimeout(() => {
        self.getQuote()
      }, 20)
    },
    optinSharedAllowedTime () {
      if (!this.editData.tripInfo.isAirportPickup) return true

      let settings = this.$store.state.settings
      if (settings.optinAllowedTime.start === settings.optinAllowedTime.end) return false

      let allowTimeStart = moment(settings.optinAllowedTime.start, 'hh:mmA')
      let allowTimeEnd = moment(settings.optinAllowedTime.end, 'hh:mmA')

      let selectedTime = moment(this.editData.tripInfo.pickupDate, 'MM/DD/YYYY ' + featureToggle.timeFormat(this, false))
      selectedTime = moment(selectedTime.format(featureToggle.timeFormat(this, false)), featureToggle.timeFormat(this, false))

      if (parseInt(selectedTime.format('X')) >= parseInt(allowTimeStart.format('X')) &&
        parseInt(selectedTime.format('X')) <= parseInt(allowTimeEnd.format('X'))
      ) {
        return true
      } else {
        this.$store.commit('rsvpDetails/setOptinData', {enabled: false})
        return false
      }
    },
    optinSharedPrice () {
      let origPrice = this._edit.services[this._edit.selectedService].prices[(this.tripInfo.passenger || 1) - 1].price
      let percent = this._edit.services[this._edit.selectedService].optinPercent / 100
      percent = parseFloat((1 - percent).toFixed(2))
      return origPrice * percent
    },
    optinRefundPrice () {
      let origPrice = this._edit.services[this._edit.selectedService].prices[(this.tripInfo.passenger || 1) - 1].price
      return parseFloat((origPrice - this.optinSharedPrice()).toFixed(2))
    },
    getTimezoneFirst () {
      let self = this
      self.$store.commit('rsvpDetails/setTimezoneLoading', true)
      ApiLocation.getTimezone(self, {
        lat: self.tripInfo.pickup.lat,
        lng: self.tripInfo.pickup.lng
      }, function (res) {
        self.timezoneInfo = {
          pickup: self.tripInfo.pickup.title,
          tz: res
        }
        self.$store.commit('rsvpDetails/setTimezoneLoading', false)
        self.$store.commit('rsvpDetails/setTimezone', res)

        self.quoteDelay = setTimeout(function () {
          if (!CONFIG.useOldQuatation) {
            self.selectedService = ''
          }
          self.getQuote()
        }, 500)
      })
    },
    changeFlightDate () {
      let self = this
      self.flightDate.date = moment(self.flightDate.date).format('MM/DD/YYYY')
      self.updateFlightDate()
      // if (
      //   self.flightDate.date && self.flightDate.hour && self.flightDate.minute &&
      //   ((this.$settings.get('MilitaryTimeBackoffice') == '0' && this.flightDate.amPm) || this.$settings.get('MilitaryTimeBackoffice') == '1')
      // ) {
      //   setTimeout(() => {
      //     self.getQuote()
      //   }, 20)
      // }
    },
    updateFlightDate () {
      let flightDate = this.flightDate.date + ' ' + this.flightDate.hour + ':' +
        this.flightDate.minute + '' + this.flightDate.amPm
      if (this.$settings.get('MilitaryTimeBackoffice') === '1') {
        flightDate = this.flightDate.date + ' ' + this.flightDate.hour + ':' + this.flightDate.minute
      }
      this.$store.commit('rsvpDetails/setMtechData', {flightDate: flightDate})
      setTimeout(() => {
        this.getQuote(true)
      }, 20)
    },
    selectMtechService (service) {
      if (this.passenger > service.maxPaxVal || this.luggage > service.maxLuggageVal) return
      
      this.selectedService = service.code
      this.isMeetAndGreet = false

      this.paymentBonusEnabled = false
      this.paymentBonus = 0
      
      this.$store.commit('rsvpDetails/setOptinData', {enabled: false})
      this.getQuoteMtechService()
    },
    getQuoteMtechService () {
      let self = this

      let rsvpEdit = self.$store.state.rsvpDetails.edit
      if (rsvpEdit.serviceLoading) { return }

      let pickupdate = moment()
      let pickuptime = moment()
      if (self.pickupDate.date && self.pickupDate.date !== 'Invalid date' && !self.pickupAsap) {
        pickupdate = moment(self.pickupDate.date, 'MM/DD/YYYY')
        pickuptime = moment(self.pickupDate.hour+':'+self.pickupDate.minute+' '+self.pickupDate.amPm, "hh:mm A")
      }

      let quoteData = {}
      quoteData = {
        brandId: self.tripInfo.brandId,
        pickupDate: pickupdate.format('YYYY-MM-DD'),
        pickup: self.tripInfo.pickup,
        dropoff: self.tripInfo.dropoff,
        asap: false,
        isAdmin: true,
        order: 'priority',
        pickupTime: pickuptime.format("HH:mm:ss"),
        mtechServiceType: self.selectedService
      }
      let tripInfo = rsvpEdit.details.tripInfo
      quoteData.pickup.text = tripInfo.pickup.title
      quoteData.dropoff.text = tripInfo.dropoff.title
      quoteData.passengers = tripInfo.passenger || 1
      if (self.mtech.flightDate) {
        quoteData.pickupTimestamp = moment.tz(self.mtech.flightDate, 'MM/DD/YYYY hh:mmA', rsvpEdit.timezone).format('X')
      } else {
        quoteData.pickupTimestamp = moment.tz(`${quoteData.pickupDate} ${quoteData.pickupTime}`, 'YYYY-MM-DD HH:mm', rsvpEdit.timezone).format('X')
      }

      self.$store.commit('rsvpDetails/setServiceLoading', true)
      let timeNow = moment()
      self.$sdk.post('/quotation', quoteData, res => {
        self.sendQuoteTime(timeNow, moment(), res.quoteId)
        self.$store.commit('rsvpDetails/setServiceLoading', false)
        if (!res.error) {
          self.$store.commit('rsvpDetails/setMtechData', {quoteId: res.quoteId})
          if (res.mtech.services.length) {
            let mtech = res.mtech
            let mtechStore = self.$store.state.rsvpDetails.mtech
            let service = res.mtech.services[0]
            let price = service.price

            let hasDiscount = false
            let discountVal = 0
            let totalBeforeDc = 0
            let baseFare = price.amount
            service.price.breakdown.forEach(x => {
              if (x.description === 'Base rate') {
                baseFare = x.amount
              }
              if (x.description === 'Discount') {
                hasDiscount = true
                discountVal = x.amount
              }
              if (x.amount > 0) {
                totalBeforeDc += x.amount
              }
            })

            let addMarkup = 0
            if (mtech.markup.value) {
              addMarkup = mtechStore.markup.value
              if (mtech.markup.type === '%') {
                addMarkup = baseFare * (mtech.markup.value / 100)
              }
              addMarkup = parseFloat(addMarkup.toFixed(2))
            }

            self.$store.commit('rsvpDetails/setMtechNewPricing', {
              code: self.selectedService,
              val: {
                baseFare: baseFare,
                amount: price.amount,
                addMarkup: addMarkup,
                breakdown: price.breakdown,
                hasDiscount: hasDiscount,
                discountVal: Math.abs(discountVal),
                totalBeforeDc: totalBeforeDc
              }
            })
          }
        }
      }, '2.0')
    },
    rechkServiceBuffer () {
      if (!this.selectedService) return
      let service = this._edit.services[this.selectedService]
      let nowPlusBuffer = moment()
      if (service && service.bufferTime) nowPlusBuffer = moment().add(service.bufferTime, 'minutes')
      let pickupDate = moment(this.tripInfo.pickupDate, 'MM/DD/YYYY hh:mmA')
      if (pickupDate.isBefore(nowPlusBuffer)) {
        this.selectedService = ''
      }
    },
    sendQuoteTime (start, end, quoteId) {
      let store = this.$store.state
      let duration = end.diff(start, 'milliseconds') / 1000
      this.$sdk.opt('/quotation/time', {
        token: store.session.sessionToken,
        brandId: this.tripInfo.brandId,
        quotationId: quoteId,
        responseTime: duration,
        deviceType: 'admin'
      }, res => {
        if (res.error) console.log('ERROR quotation time')
      })
    },
    selectCountry() {
      this.$store.commit('countryCode/setCountryModal', true)
    },
    applyCurrency (x, useSymbol = true) {
      x = parseFloat(x)
      x = x * this.currency.value
      // return useSymbol ? `${this.currency.symbol}${x.toFixed(2)}` : x.toFixed(2)
      if (!useSymbol) return x.toFixed(2)
      return xcurrency.format({
        code: this.currency.code,
        val: x.toFixed(2)
      }, useSymbol)
    },
    changeBrand () {
      this.bookingType = 'mileage'
      this.getQuote()
    },
    setLocations (tripInfo, getQuote = false) {
      this.customPickupLocation = {title: ''}
      this.customDropoffLocation = {title: ''}

      this.isCustomPickupLocation = true
      this.isCustomDropoffLocation = true
      tripInfo.pickup.address2 = tripInfo.pickup.address2.split(' Apt/Gate:')[0]
      tripInfo.pickup.title = tripInfo.pickup.title.split(' Apt/Gate:')[0]
      tripInfo.dropoff.address2 = tripInfo.dropoff.address2.split(' Apt/Gate:')[0]
      tripInfo.dropoff.title = tripInfo.dropoff.title.split(' Apt/Gate:')[0]
      let self = this
      self.$nextTick(() => {
        self.customPickupLocation = tripInfo.pickup
        self.customDropoffLocation = tripInfo.dropoff
        if (getQuote) self.getQuote()
      })

    },
    cleanCountryName (val) {
      let val2 = val.slice(0, val.indexOf(" ("))
      return val.indexOf(" (") === -1 ? val : val2
    },
    generatePassword () {
      var result = ''
      var characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
      var charactersLength = characters.length
      for ( var i = 0; i < 8; i++ ) {
        result += characters.charAt(Math.floor(Math.random() * charactersLength))
      }
      return result
    },
    createD2Duser () {
      let self = this
      if (self.d2dUser.loading) return
      self.d2dUser.loading = true

      self.$store.commit('global/setLoaderData', {show: true})

      let countryCode = this.callingCode
      let nationalNumber = this.createUser.mobile
      let bday = new Date(this.d2dUser.birthday)
      let passwrd = self.generatePassword()
      self.$sdk.post('/consumer', {
        token: self.$store.state.session.sessionToken,
        email: self.createUser.email,
        password: passwrd,
        device: `DEVICE-${moment().format('x')}-${self.generatePassword()}`,
        firstName: self.createUser.firstName,
        lastName: self.createUser.lastName,
        phone: `+${countryCode}${nationalNumber}`,
        countryCode: countryCode,
        nationalNumber: nationalNumber,
        email: self.createUser.email,
        wheelchair: self.wheelchairEnabled,
        serviceDog: self.serviceDogEnabled,
        brandId: self.tripInfo.brandId,
        dateOfBirth: moment(bday).format('YYYY-MM-DD'),
        userCountry: self.d2dUser.country,
        passportNum: self.d2dUser.passport || '',
        d2d: true
      }, r => {
        self.d2dUser.loading = false
        if (r.error) {
          self.$store.commit('global/setLoaderData', {show: false})
          self.$store.commit('global/setAlertData', {
            show: true,
            message: r.error.message
          })
          return
        }

        self.createUser.show = false
        self.$store.commit('rsvpDetails/setCustomerInfo', {
          id: r.ID,
          firstName: r.firstName,
          lastName: r.lastName,
          email: r.email,
          mobile: r.phone,
          countryCode: self.callingCode,
          nationalNumber: self.createUser.mobile
        })

        self.$store.commit('rsvpDetails/setIsCorporate', false)
        self.resetPaymentMethod()
        self.firstD2DLogin({
          id: r.ID,
          email: self.createUser.email,
          password: passwrd
        })
        // self.d2dStripeSetup(r.ID)
      }, '1.1')
    },
    firstD2DLogin (user) {
      let self = this
      self.$sdk.post('/auth/login', {
        d2d: 1,
        device: `DEVICE-${moment().format('x')}-${self.generatePassword()}`,
        deviceType: 'admin',
        email: user.email,
        password: user.password
      }, r => {
        self.d2dStripeSetup(user.id)
      })
    },
    d2dStripeSetup (id) {
      let self = this
      self.$sdk.post(`/consumer/${id}/cards/setup`, {
        token: self.$store.state.session.sessionToken
      }, r => {
        if (r.error) {
          self.$store.commit('global/setLoaderData', {show: false})
          self.$store.commit('global/setAlertData', {
            show: true,
            message: r.error.message
          })
          return
        }

        if (self.$refs.stripeForm) self.$refs.stripeForm.submitStripe(id, r.client_secret)
      }, '1.1')
    },
    setNewD2DCard () {
      this.createUser.paymentMethod = 'oldCard'
      this.createUser.selectedCard = 0

      this.createRsvp()
    }
  },
  watch: {
    editData: function (value) {
      this.selectedService = value.tripInfo.serviceCode
      // this.isCustomPickupLocation = true
      // this.isCustomDropoffLocation = true
      // value.tripInfo.pickup.address2 = value.tripInfo.pickup.address2.split(' Apt/Gate:')[0]
      // value.tripInfo.pickup.title = value.tripInfo.pickup.title.split(' Apt/Gate:')[0]
      // this.customPickupLocation = value.tripInfo.pickup
      // value.tripInfo.dropoff.address2 = value.tripInfo.dropoff.address2.split(' Apt/Gate:')[0]
      // value.tripInfo.dropoff.title = value.tripInfo.dropoff.title.split(' Apt/Gate:')[0]
      // this.customDropoffLocation = value.tripInfo.dropoff
      this.setLocations(value.tripInfo)
      this.isMeetAndGreet = value.tripInfo.meetAndGreet

      if (value.tripInfo.pickup.geofenceType === 'airport') {
        this.customPickupLocation = ''
        this.isCustomPickupLocation = false
        this.pickupAirportId = value.tripInfo.pickup.geofenceId
      }
      if (value.tripInfo.dropoff.geofenceType === 'airport') {
        this.customDropoffLocation = ''
        this.isCustomDropoffLocation = false
        this.dropoffAirportId = value.tripInfo.dropoff.geofenceId
      }
      if (value.tripInfo.isCharter) {
        this.bookingType = 'charter'
      }
      this.waivePayments = false
    },
    tripInfo: function (value) {
      this.wheelchairEnabled = value.wheelchair
      this.serviceDogEnabled = value.serviceDog
      if (value.meetAndGreet) { this.isMeetAndGreet = true }

      this.priceOverrideEnabled = false
      this.priceOverride = ''
      if (value.overridePrice) {
        this.priceOverride = value.overridePrice
        this.priceOverrideEnabled = true
      }

      this.driverOverrideEnabled = false
      this.driverOverride = ''
      if (value.overridePayment) {
        this.driverOverrideEnabled = true
        this.driverOverride = value.overridePayment
      }

      this.paymentBonusEnabled = false
      this.paymentBonus = 0
      if (value.paymentBonus) {
        this.paymentBonusEnabled = true
        this.paymentBonus = value.paymentBonus
      }
    },
    guestInfo: function (value) {
      this.bookingForSomeone = false
      if (value.firstName) { this.bookingForSomeone = true }
    },
    isCustomPickupLocation: function (value) {
      if (this.$store.state.rsvpDetails.basicInfo.tripInfo.pickup.geofenceType === 'airport' && value) {
        this.driverOverrideValidation.pickupAirport = false
      } else if (this.$store.state.rsvpDetails.basicInfo.tripInfo.pickup.geofenceType === 'airport') {
        this.driverOverrideValidation.pickupAirport = true
      }
      if (this.$store.state.rsvpDetails.basicInfo.tripInfo.pickup.geofenceType === 'servicearea' && !value) {
        this.driverOverrideValidation.servicePickup = false
      } else if (this.$store.state.rsvpDetails.basicInfo.tripInfo.pickup.geofenceType === 'servicearea') {
        this.driverOverrideValidation.servicePickup = true
      }
      this.clearDriverPayment()

      if (!value) {
        this.validation.pickupLocation = ''
        this.selectAirportPickup(this.pickupAirportId)
      } else {
        if (this.$refs.customPickupLoc.clickOut()) {
          this.$refs.customPickupLoc.clickOut()
        }
      }

    },
    isCustomDropoffLocation: function (value) {
      if (this.$store.state.rsvpDetails.basicInfo.tripInfo.dropoff.geofenceType === 'airport' && value) {
        this.driverOverrideValidation.dropoffAirport = false
      } else if (this.$store.state.rsvpDetails.basicInfo.tripInfo.dropoff.geofenceType === 'airport') {
        this.driverOverrideValidation.dropoffAirport = true
      }
      if (this.$store.state.rsvpDetails.basicInfo.tripInfo.dropoff.geofenceType === 'servicearea' && !value) {
        this.driverOverrideValidation.serviceDropoff = false
      } else if (this.$store.state.rsvpDetails.basicInfo.tripInfo.dropoff.geofenceType === 'servicearea') {
        this.driverOverrideValidation.serviceDropoff = true
      }
      // this.clearDriverPayment()

      if (!value) {
        this.validation.dropoffLocation = ''
        this.selectAirportDropoff(this.dropoffAirportId)
      } else {
        if (this.$refs.customDropoffLoc.clickOut()) {
          this.$refs.customDropoffLoc.clickOut()
        }
      }
    },
    airportPickup: function (value) {
      if (value) { this.getAirlineList() }
    },
    airportDropoff: function (value) {
      if (value && !this.airportPickup) { this.getAirlineList() }
    },
    bookingForSomeone: function (value) {
      if (!value) {
        this.editData.guestInfo.firstName = ''
        this.editData.guestInfo.lastName = ''
        this.editData.guestInfo.mobile = ''
      }
    },
    selectedService: function (value) {
      this.$store.commit('rsvpDetails/setSelectedService', value)
      this.setDefaultTip()
    },
    'customerInfo.firstName': function (value) {
      if (value) {
        if (!this.isCorporate) {
          this.createUser.paymentMethod = 'newCard'
        }
      }
    },
    pickupAsap: function (value) { this.$store.commit('rsvpDetails/setAsapEnable', value) },
    passenger: function (value) {
      this.setDefaultTip()
      if (this.mtech.enabled) {
        this.getQuote()
      }
    },
    bookingType (val) {
      this.$store.commit('rsvpDetails/setCharterData', {enabled: val === 'charter'})
      this.pickupAsap = false
      if (val === 'charter') {
        this.passenger = 1
        this.optinEnabled = false
      }
    },
    extraStops (val) {
      if (val.length) {
        this.optinEnabled = false
      }
    },
    'tripInfo.pickupDate': function () {
      this.rechkServiceBuffer()
    },
    tipIntnl (val) {
      if (this.currencyCode === 'USD') return
      this.editData.tripInfo.tip = parseFloat((parseFloat(val) / this.currency.value).toFixed(2))
    },
    priceOverrideIntnl (val) {
      this.priceOverride = parseFloat((parseFloat(val) / this.currency.value).toFixed(2))
    },
    waivePayments (val) {
      if (val) {
        this.setLocations(this.$store.state.rsvpDetails.basicInfo.tripInfo, true)
      }
    }
  },
  mounted() {
    this.setScreenSize()
    this.originalPax = this.passenger
  },
  created() {
    window.addEventListener("resize", this.setScreenSize);
  },
  destroyed() {
    window.removeEventListener("resize", this.setScreenSize);
  },
}
</script>

<style lang="scss" scoped>
@import '../../assets/scss/_creditCardIcons';
* { -webkit-overflow-scrolling: touch; }

.showDesktop, .showMobile { display: block; }

.hideDesktop, .hideMobile { display: none; }

.row{
  margin: 0 0 20px;
  padding-top: 10px;
  width: 100%!important;
  max-width: 100%;
  clear:both;

  &.row-d2d{
    margin: 0 0 5px;
  }
}

.margin-T { margin-top: 10px; }
.margin-xT { margin-top: 50px; }
.margin-B { margin-bottom: 10px!important; }
.margin-R { margin-right: 10px; }
.padding-N-T { padding-top: 0; }
.padding-R { padding-right: 10px; }
.padding-xR { padding-right: 30px; }
.padding-L { padding-left: 10px; }
.padding-xL { padding-left: 30px; }
.right{ float:right; }
.clearfix{ clear:both; }
.pointer{ cursor:pointer }

.heading{
  font-size: 24px;
  color: #57D1B7;
  font-weight: 600;
}

.data-field{
  padding-right: 10px;
  position: relative;

  &.no-label{ padding-top: 17px; }
  .label{
    color: #888;
    font-weight: 600;
    font-size: 12px;
    text-transform: uppercase;
    line-height: 130%;

    &.has-error{ color: #f20d0d; }
    &.bigger{ font-size: 14px; }
    small{
      color: #bbb;
      margin-left: 5px;
      vertical-align: 2px;
    }
  }
  .value{
    font-weight: 600;
    font-size: 17px;
    color: #555;

    a{
      text-decoration: underline;
      cursor: pointer;
      word-wrap: break-word;
    }
  }
  .description{
    font-size: 11px;
    color: #999;
    font-style: italic;
    display: block;
    color: #ff8d00;
  }
  .input{
    margin: 5px 0 0;
    color: #333;
    line-height: inherit;
    transition: all .35s ease;

    &.has-error{ border-color: #f20d0d; }
    &:disabled{ background: #eee; }
  }
  textarea{
    border: 1px solid #ddd;
    width: 100%;
    height: 100px;
    resize: none;
    padding: 10px;
    font-size: 13px;
    color: #555;
    outline: none;
    margin: 5px 0 0;
  }
  select{
    margin-top: 5px;
    border: 1px solid #ddd;
    padding: 10px;
    color: #333;
    height: 45px;
    position: relative;
    font-size:14px;
    outline: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    background-image: url('/assets/img/dropdown-arrow.png');
    background-repeat: no-repeat;
    background-position: right 50%;
    transition: all .35s ease;

    &.has-error{ border-color: #f20d0d; }
    &:disabled{ background-color: #eee; }
  }
  .checkbox{
    line-height: 100%;
    margin-bottom: 3px;

    &.inline{
      display:inline-block;
      margin: 0 25px 10px 0;
    }
    &:last-child{ margin-bottom: 0; }
    input{
      display: inline-block;
      width: auto;
      vertical-align: -2px;
      margin: 0 5px 0 0;
    }
    label{
      font-size: 14px;
      color: #555;
      line-height: 150%;
    }
  }
  .radio{
    display: block;
    color: #333;
    font-size: 14px;
    margin-top: 5px;

    &:first-child{ margin-top: 5px; }
    input[type="radio"]{
      display: inline-block;
      width: auto;
      margin: 0 0 0 5px;
      vertical-align: middle;
      cursor: pointer;
    }
    .radio-fa{
      margin: 20px 10px 0 0;
      color: #aaa;
      transition: all .5s ease;

      &.checked{
        color:#57d1b7;
        &:before{
          content: "\f192";
        }
      }
    }
    &.with-textbox{
      display: flex;

      input[type="radio"]{ margin: 15px 10px 0 0; }
      .input{
        display: inline-block;
        vertical-align: middle;
      }
    }
    &.label-only{
      .radio-fa{ margin: 0 10px 10px 0; }
    }
  }
  .error-msg{
    font-size: 11px;
    line-height: 1.6;
    color: #f20d0d;
    font-style: italic;
    display: block;
  }
}

.datepicker{ position: relative; }

.pickup-time{
  .vdp-datepicker{
    margin-bottom: 5px;
  }
  .datepicker{
    margin-right: 25px;
    display: inline-block;
  }
  select{
    display: inline-block;
    width: auto;
    min-width: 70px;
    margin-top: 0;
  }
}

.apt-gate{ padding-left: 23px; }

.cc-icon{
  margin-right: 5px;
  vertical-align: middle;
}

.pricing{
  padding: 5px 10px;
  border-radius: 4px;
  transition: all .15s ease;
  cursor: pointer;
  border:1px solid #777;
  clear:both;
  opacity: .65;
  background: #fafafa;
  color: #999;
  min-height: 55px;

  &:hover, &.active{ opacity: 1; }
  &.active{
    border: 1px solid #0D79F2;
    background: #e6f6ff;
    color: #333;
    box-shadow: 2px 2px 4px rgba(0,0,0, .35);
  }
  &.disabled{
    cursor: default;
    opacity: .35;
  }
  /*.radio-fa{ margin: 0 10px 0 0!important; }
  input[type="radio"]{
    margin-right: 5px!important;
  }*/
  .service-desc{

    .service-label{
      font-weight: 600;
      font-size: 18px;
      display: block;
    }
    .rule-value{
      display: inline-block;
      margin-right: 10px;
    }
    .pax-luggage{
      .item{ display: inline-block; }
      .disabled{ color: #f00; }
    }
  }
  .value{
    float:right;
    font-weight: 600;
    font-size: 32px;
    display: inline-block;

    &.has-discount:not(.price-override){
      font-size: 24px;

      div{
        font-size: 14px;
        line-height: 1;
        text-align: center;
        color: #999;

        span{
          &:after{
            content: '';
            border-top:1px solid #f00;
            position: absolute;
            top: 50%;
            left: 0;
            width: 100%;
            height: 2px;
          }
        }
      }
      span{
        position: relative;
        padding: 0 5px;
      }
    }
    &.price-override{ color: #923bbd; }
  }
  .unavailable {
    .availability {
      float: right;
      font-weight: 600;
      font-size: 32px;
      display: grid;
      text-align: center;
      .available {
        font-size: 14px;
        padding: 2px;
      }
      .date-time {
        display: block;
        font-size: 14px;
        font-weight: 900;
      }
    }
  }
}

.input-group {
  display: -webkit-box;
  display: -webkit-flex;
  display: -ms-flexbox;
  display: flex;
  width: 100%;
  -webkit-box-align: stretch;
  -webkit-align-items: stretch;
  -ms-flex-align: stretch;
  align-items: stretch;
  position: relative;
  margin-bottom: 0;
  margin-top: 5px;

 .input-group-label {
  padding: 0 10px;
  border: 1px solid #cacaca;
  background: #f4f8f9;
  color: #0a0a0a;
  text-align: center;
  white-space: nowrap;
  cursor: pointer;
  display: -webkit-box;
  display: -webkit-flex;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-flex: 0;
  -webkit-flex: 0 0 auto;
  -ms-flex: 0 0 auto;
  flex: 0 0 auto;
  -webkit-box-align: center;
  -webkit-align-items: center;
  -ms-flex-align: center;
  align-items: center;
 }
 input{
  border-radius: 0 3px 3px 0;
  margin-top:0 !important
 }
}

.currency{
  position: relative;

  input{
    position: relative;
    z-index: 1;
    padding-left: 20px;
  }
  span{
    position: absolute;
    top:0;
    left: 5px;
    z-index: 2;
    font-size: 21px;
    padding: 10px 0;
    line-height: 125%;
    color: #ccc;
  }
  .amount {
    color: #000;
  }
}

.custom-check{
  cursor: pointer;
  display: inline-block;
  margin-right: 3px;
  vertical-align: middle;

  div{
    border:2px solid #ccc;
    width: 22px;
    height: 22px;
    line-height: 1;
    text-align: center;
    padding: 1px 0;
    border-radius: 3px;
    transition: all .25s ease-out;

    i{ color: #fff; }
  }
  &.enabled div{
    background: #57d1b7;
    border-color: #57d1b7;
  }
}

.customer-info{
  .row{ margin: 0 0 5px; }
  .guest{ margin: 25px 0; }
}

.payment-summary{
  border: 1px solid #aaa;
  border-radius: 6px;
  overflow: hidden;
  background: #fdfdfd;
  margin-top: 50px;
  box-shadow: 0 0 2px rgba(0,0,0, .2);

  .row{ padding: 0; }
  .title{
    display: block;
    text-align: center;
    font-weight: 600;
    font-size: 18px;
    color: #fff;
    padding: 5px 0;
    background: #444;
  }
  .stats{
    padding: 10px;
    font-size: 14px;
    margin: 0 15px;
    border-bottom: 1px solid #f0f0f0;

    &.hard-border{ border-bottom: 1px solid #bbb; }
    .label{
      color: #aaa;
      vertical-align: middle;
    }
    .value{
      float: right;
      font-weight: 600;
      color: #555;

      &.price-override{ color:#923bbd; }
      &.total{
        font-size: 18px;
        color: #333;
      }
    }

  }
  .columns:last-child .stats{ border-bottom: none; }
}

.waive-payment{
  label{
    color: #888;
    font-weight: 600;
    font-size: 14px;
    letter-spacing: .5px;
    vertical-align: -1px;
    cursor: pointer;
  }
}

.service-field{
  position: relative;

  .service-loader{
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 2;

    .fa-spinner{
      color: #00b6f1;
      position: absolute;
      top: 50%;
      left: 50%;
      margin-top: -40px;
      margin-left: -40px;
    }
  }
}

.customer-option{
  padding: 10px 0;

  .or{
    display: inline-block;
    margin: 0 10px;
    color: #888;
    font-size: 14px;
  }
}

.payment-line{
  text-align: center;

  &:after{
    position: absolute;
    top: 50%;
    left: 0;
    height: 1px;
    width: 100%;
    border-top: 1px solid #c1c1c1;
    content: ' ';
    z-index: 1;
  }
  span{
    background: #fff;
    padding: 0 10px;
    position: relative;
    z-index: 2;
  }
}

.payment-option-box{
  .fa-circle{
    margin-top: 3px!important;
  }
}
.payment-option{
  vertical-align: middle;
  display: block;
  font-size: 13px;
  cursor: pointer;
}

.cc-form{
  transform-origin: top center;
  display: none;
  padding: 10px;
  background: #f5f5f5;

  &.show{ display: block; }
}

.paid-notes{
  &.has-error{
    border:1px solid #f20d0d;
  }
}

.notes-char-count{
  font-size: 12px;
  display: block;
  float: right;
  text-align: right;
  color: #555;
}

.select-card{
  font-size: 11px;
  line-height: 1.6;
  color: #f20d0d;
  font-style: italic;
  display: block;
}

.send-email{ margin-top: 40px; }
.asap{ margin: 10px 0; }

.geofence-label{
  font-size: 13px;
  margin-bottom: 10px;
  color: #444;

  b{ margin-left: 5px; }
}

.add-extra-stop{
  font-size: 13px;
  color: #2586f3;
  font-weight: 600;

  span{
    cursor:pointer;
  }
  i{
    margin-right: 5px;
  }
}
.extra-stop-notice{
  color: #999;
  font-style: italic;
  font-size: 13px;
  margin-bottom: 3px;
}

.optin-toggle{
  font-size: 14px;
  cursor: pointer;

  input{
    width: auto;
    display: inline-block;
    vertical-align: -2px;
  }
}
.optin-desc{
  background: #fafafa;
  border: 1px solid #424769;
  border-radius: 4px;
  color: #424769;
  opacity: .6;
  
  &.active{
    opacity: 1;
  }
  .opt-copy{
    padding: 5px 10px;
    font-size: 14px;

    .head{
      font-weight: 600;
      font-size: 18px;
    }
  }
  .flex{  
    padding: 5px 10px;
    display: flex;
  }
  .info{
    width: 100%;

    .service-name{
      font-size: 18px;
    }
  }
  .capacity{
    i:last-child{
      margin-left: 10px;
    }
  }
  .availability{
    div{
      padding: 4px;
    }
  }
}
.optin-box{
  padding-top: 0;
  margin-bottom: 10px!important;
  .price span{
    font-weight: 600;
    font-size: 32px;
  }
  .availability{
    font-size: 14px;
    text-align: center;
    background: #424769;
    color: #fff;
    
    b{ margin-left: 5px; }
  }
}

.padding-N-r{ padding-right: 0; }
.margin-B-10{ margin-bottom: 10px; }
.margin-B-0{ margin-bottom: 0; }

.slide-down-enter-active, .slide-down-leave-active { transition: all .3s ease; }
.slide-down-enter, .slide-down-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

.show-down-enter-active, .show-down-leave-active {
  transition: height 1s ease-in-out;
  overflow: hidden;
}
.show-down-enter, .show-down-leave-to { height: 0; }

.popin-enter-active, .popin-leave-active {
  transition: all .3s ease;
  transition-delay: .3s;
}
.popin-enter, .popin-leave-to {
  transform: scale(0.8);
  opacity: 0;
}
@media only screen and (max-width: 430px) {
  .row {
    margin-bottom: 5px;
    padding-top: 5px;
  }
  .data-field {
    margin-top: 5px;
    padding-right: 5px;
  }

  .heading {
    font-size: 18px;
    text-transform: uppercase;
    font-weight: 900;

    &.margin-xT {
      margin-top: 20px;
    }
  }

  .send-email {
    margin-top: 10px;
  }

  .payment-summary {
    margin-top: 20px;
    margin-bottom: 50px;
  }

  .pickup-time {
    .datepicker {
      margin-right: 0px;
      display: block;
    }

    .medium-8 {
      display: flex;
      gap: 5px;
      flex-wrap: wrap;
      justify-content: center;

      select {
        width: 32%;
      }
    }
  }

  .padding-xL,
  .padding-xR {
    padding: 0;
  }

  .customer-option {
    .cta {
      margin: 5px 0;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/RsvpDetails/Edit.vue